import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Users, BookOpen, Plus, Edit, Languages, Image as ImageIcon, Upload, UserPlus, Check, X, FileText, Shield, UserCog, Music } from "lucide-react";
import { AnnouncementsManagement } from "@/components/admin/AnnouncementsManagement";
import { CourseDocuments } from "@/components/admin/CourseDocuments";
import { BibleSchoolGallery } from "@/components/admin/BibleSchoolGallery";
import { MemberDirectory } from "@/components/admin/MemberDirectory";
import { MusicManagement } from "@/components/admin/MusicManagement";
import YouTubeVideosManagement from "@/pages/admin/YouTubeVideos";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { PhoneInput } from "@/components/ui/phone-input";
import { formatDateForDisplay, parseDateAsLocal } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

interface Donation {
  id: string;
  amount: number;
  category: string;
  donor_name: string;
  donor_email: string;
  created_at: string;
  status: string;
  processing_fee: number;
}

interface AppUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  roles?: { role: string; is_primary?: boolean }[];
}

interface Sermon {
  id: string;
  title: string;
  description: string | null;
  sermon_date: string;
  speaker: string;
  audio_url: string | null;
  video_url: string | null;
  scripture_reference: string | null;
  created_at: string;
  published?: boolean;
  created_by?: string;
  translations?: Record<string, SermonTranslation>;
}

interface SermonTranslation {
  id?: string;
  sermon_id: string;
  language: string;
  title: string;
  description: string;
  scripture_reference: string;
  speaker: string;
}

interface MissionTrip {
  id: string;
  mission: string;
  title: string;
  description: string | null;
  trip_date: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
  photos?: MissionPhoto[];
}

interface MissionPhoto {
  id: string;
  mission: string;
  trip_id: string | null;
  title: string;
  description: string | null;
  image_url: string;
  video_url: string | null;
  media_type: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface SpecialEvent {
  id: string;
  title: string;
  description: string | null;
  event_date: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
  media?: SpecialEventMedia[];
}

interface SpecialEventMedia {
  id: string;
  event_id: string;
  title: string;
  description: string | null;
  image_url: string;
  video_url: string | null;
  media_type: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  author: string;
  category: string;
  excerpt: string | null;
  content: string;
  featured_image_url: string | null;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
  translations?: Record<string, BlogTranslation>;
}

interface BlogTranslation {
  id?: string;
  blog_post_id: string;
  language: string;
  title: string;
  excerpt: string | null;
  content: string;
  author: string;
}

interface Course {
  id: string;
  title: string;
  description: string;
  instructor: string;
  duration: string;
  level: string;
  schedule: string | null;
  max_students: number | null;
  image_url: string | null;
  published: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  translations?: Record<string, CourseTranslation>;
}

interface CourseTranslation {
  id?: string;
  course_id: string;
  language: string;
  title: string;
  description: string;
  instructor: string;
  duration: string;
  level: string;
  schedule: string | null;
}

interface ContributorRequest {
  id: string;
  email: string;
  full_name: string;
  message: string | null;
  requested_role: string;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

const AdminDashboard = () => {
  const { user, isAdmin, isViewer, isBlogEditor, isBibleSchoolEditor, isAnnouncementEditor, isSermonEditor, isMusicEditor, isMusicViewer } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [donations, setDonations] = useState<Donation[]>([]);
  const [filteredDonations, setFilteredDonations] = useState<Donation[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [adminCount, setAdminCount] = useState(0);
  const [isPrimaryAdmin, setIsPrimaryAdmin] = useState(false);
  const [sermons, setSermons] = useState<Sermon[]>([]);
  const [isLoadingSermons, setIsLoadingSermons] = useState(false);
  const [isSermonDialogOpen, setIsSermonDialogOpen] = useState(false);
  const [editingSermon, setEditingSermon] = useState<Sermon | null>(null);
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    email: "",
    password: "",
    role: "none" as "none" | "admin" | "viewer" | "blog_editor" | "bible_school_editor" | "announcement_editor" | "sermon_editor" | "member" | "music_editor" | "music_viewer"
  });
  const [isMemberFormDialogOpen, setIsMemberFormDialogOpen] = useState(false);
  const [memberFormData, setMemberFormData] = useState({
    user_id: "",
    full_name: "",
    email: "",
    phone: "",
    address: "",
    number_of_kids: 0,
    date_of_birth: "",
  });
  const [isTranslationsDialogOpen, setIsTranslationsDialogOpen] = useState(false);
  const [translatingSermon, setTranslatingSermon] = useState<Sermon | null>(null);
  const [translations, setTranslations] = useState<Record<string, SermonTranslation>>({});
  const [sermonForm, setSermonForm] = useState({
    title: "",
    description: "",
    sermon_date: "",
    speaker: "",
    audio_url: "",
    video_url: "",
    scripture_reference: "",
    published: false,
  });
  const [missionPhotos, setMissionPhotos] = useState<MissionPhoto[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [selectedMission, setSelectedMission] = useState<'guatemala' | 'mexico' | 'cuba'>('guatemala');
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState<MissionPhoto | null>(null);
  const [photoForm, setPhotoForm] = useState({
    title: "",
    description: "",
    display_order: 0,
    video_url: "",
  });
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadingFile, setCurrentUploadingFile] = useState("");
  
  // Mission Trips state
  const [missionTrips, setMissionTrips] = useState<MissionTrip[]>([]);
  const [isLoadingTrips, setIsLoadingTrips] = useState(false);
  const [isTripDialogOpen, setIsTripDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<MissionTrip | null>(null);
  const [tripForm, setTripForm] = useState({
    title: "",
    description: "",
    trip_date: "",
    display_order: 0,
  });
  const [selectedTrip, setSelectedTrip] = useState<string | null>(null);
  
  // Special Events state
  const [specialEvents, setSpecialEvents] = useState<SpecialEvent[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<SpecialEvent | null>(null);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    event_date: "",
    display_order: 0,
  });
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [eventMedia, setEventMedia] = useState<SpecialEventMedia[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
  const [editingMedia, setEditingMedia] = useState<SpecialEventMedia | null>(null);
  const [mediaForm, setMediaForm] = useState({
    title: "",
    description: "",
    display_order: 0,
    video_url: "",
  });
  const [selectedMedia, setSelectedMedia] = useState<string[]>([]);
  const [isMediaSelectMode, setIsMediaSelectMode] = useState(false);
  
  // Blog state
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(false);
  const [isBlogDialogOpen, setIsBlogDialogOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<BlogPost | null>(null);
  const [isBlogTranslationsDialogOpen, setIsBlogTranslationsDialogOpen] = useState(false);
  const [translatingBlog, setTranslatingBlog] = useState<BlogPost | null>(null);
  const [blogTranslations, setBlogTranslations] = useState<Record<string, BlogTranslation>>({});
  const [blogForm, setBlogForm] = useState({
    title: "",
    slug: "",
    author: "",
    category: "",
    excerpt: "",
    content: "",
    featured_image_url: "",
    published: false,
  });
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [blogStatusFilter, setBlogStatusFilter] = useState<"all" | "published" | "draft">("all");
  const [courseStatusFilter, setCourseStatusFilter] = useState<"all" | "published" | "draft">("all");

  // Function to generate URL-friendly slug from title
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
  };

  // Filter blog posts based on status
  const filteredBlogPosts = blogPosts.filter(blog => {
    if (blogStatusFilter === "published") return blog.published;
    if (blogStatusFilter === "draft") return !blog.published;
    return true; // "all"
  });

  // Bible School (Courses) state
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);

  // Filter courses based on status
  const filteredCourses = courses.filter(course => {
    if (courseStatusFilter === "published") return course.published;
    if (courseStatusFilter === "draft") return !course.published;
    return true; // "all"
  });
  const [isCourseDialogOpen, setIsCourseDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [isCourseTranslationsDialogOpen, setIsCourseTranslationsDialogOpen] = useState(false);
  const [translatingCourse, setTranslatingCourse] = useState<Course | null>(null);
  const [courseTranslations, setCourseTranslations] = useState<Record<string, CourseTranslation>>({});
  const [isCourseDocumentsDialogOpen, setIsCourseDocumentsDialogOpen] = useState(false);
  const [managingCourseId, setManagingCourseId] = useState<string | null>(null);
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    instructor: "",
    duration: "",
    level: "",
    schedule: "",
    max_students: "",
    image_url: "",
    published: true,
  });

  // Contributor Requests state
  const [contributorRequests, setContributorRequests] = useState<ContributorRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  
  // Site Content state
  const [siteContent, setSiteContent] = useState<any[]>([]);
  const [groupedSections, setGroupedSections] = useState<Record<string, any[]>>({});
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isContentDialogOpen, setIsContentDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<any | null>(null);
  const [contentForm, setContentForm] = useState({
    section: "",
    content_key: "",
    content_value: "",
    language: "en",
  });
  const [selectedContentSection, setSelectedContentSection] = useState<string>("all");
  const [availableSections, setAvailableSections] = useState<string[]>([]);
  const [availableContentKeys, setAvailableContentKeys] = useState<string[]>([]);
  const [isSectionFieldsDialogOpen, setIsSectionFieldsDialogOpen] = useState(false);
  const [selectedSectionFields, setSelectedSectionFields] = useState<any[]>([]);
  const [editingSectionName, setEditingSectionName] = useState<string>("");
  const [isAddingNewSection, setIsAddingNewSection] = useState(false);
  const [isAddingNewKey, setIsAddingNewKey] = useState(false);
  const [customSection, setCustomSection] = useState("");
  const [customKey, setCustomKey] = useState("");
  const [fieldValueOptions, setFieldValueOptions] = useState<Record<string, string[]>>({});
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, boolean>>({});
  const [keyGroups, setKeyGroups] = useState<Record<string, string[]>>({});
  const [selectedKeyGroup, setSelectedKeyGroup] = useState("");
  const [savingProgress, setSavingProgress] = useState(0);
  const [savingMessage, setSavingMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [sermonSavingProgress, setSermonSavingProgress] = useState(0);
  const [blogSavingProgress, setBlogSavingProgress] = useState(0);
  const [courseSavingProgress, setCourseSavingProgress] = useState(0);
  const [selectedKeySuffix, setSelectedKeySuffix] = useState("");
  const [isBulletedContent, setIsBulletedContent] = useState(false);
  const [bulletedPrefix, setBulletedPrefix] = useState("");
  const [bulletedHeading, setBulletedHeading] = useState("");
  const [bulletedTexts, setBulletedTexts] = useState<string[]>([""]);
  const [isLoadingBulletedContent, setIsLoadingBulletedContent] = useState(false);
  
  // Watch for bulleted heading selection and load existing text fields
  useEffect(() => {
    const loadBulletedContent = async () => {
      if (!selectedKeySuffix || !contentForm.section || isAddingNewKey) return;
      
      // Check if selected key is a bulleted heading
      const bulletedMatch = selectedKeySuffix.match(/^(bulleted\d+)\.heading$/);
      if (!bulletedMatch) {
        // Reset bulleted state if not a bulleted heading
        if (isBulletedContent) {
          setIsBulletedContent(false);
          setBulletedPrefix("");
          setBulletedHeading("");
          setBulletedTexts([""]);
        }
        return;
      }
      
      const prefix = bulletedMatch[1];
      setIsLoadingBulletedContent(true);
      
      try {
        // Fetch all content with this prefix for the selected section and language
        const { data, error } = await supabase
          .from('site_content')
          .select('content_key, content_value')
          .eq('section', contentForm.section)
          .eq('language', contentForm.language)
          .like('content_key', `${prefix}.%`)
          .order('content_key', { ascending: true });
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          // Find heading
          const headingItem = data.find(item => item.content_key === `${prefix}.heading`);
          
          // Find all text items
          const textItems = data
            .filter(item => item.content_key.match(/^bulleted\d+\.text\d+$/))
            .sort((a, b) => {
              const aNum = parseInt(a.content_key.match(/text(\d+)$/)?.[1] || '0');
              const bNum = parseInt(b.content_key.match(/text(\d+)$/)?.[1] || '0');
              return aNum - bNum;
            });
          
          // Populate the form
          setIsBulletedContent(true);
          setBulletedPrefix(prefix);
          setBulletedHeading(headingItem?.content_value || '');
          setBulletedTexts(textItems.length > 0 ? textItems.map(item => item.content_value) : ['']);
          
          // Update the content form with heading value
          setContentForm(prev => ({
            ...prev,
            content_key: selectedKeySuffix,
            content_value: headingItem?.content_value || ''
          }));
        }
      } catch (error) {
        console.error('Error loading bulleted content:', error);
      } finally {
        setIsLoadingBulletedContent(false);
      }
    };
    
    loadBulletedContent();
  }, [selectedKeySuffix, contentForm.section, contentForm.language, isAddingNewKey]);
  
  // Fetch content keys filtered by selected section
  useEffect(() => {
    const fetchSectionKeys = async () => {
      if (!contentForm.section || editingContent) return;
      
      // Reset selected keys when section changes
      setSelectedKeyGroup("");
      setSelectedKeySuffix("");
      
      const { data: keysData } = await supabase
        .from('site_content')
        .select('content_key')
        .eq('section', contentForm.section)
        .order('content_key', { ascending: true });
      
      if (keysData) {
        const uniqueKeys = [...new Set(keysData.map(item => item.content_key))];
        setAvailableContentKeys(uniqueKeys);
        
        // For section-filtered keys, just put them all in "section_keys" group
        // since they don't include the section prefix
        const groups: Record<string, string[]> = {
          'section_keys': uniqueKeys
        };
        setKeyGroups(groups);
      }
    };
    
    fetchSectionKeys();
  }, [contentForm.section, editingContent, isContentDialogOpen]);
  
  // Tab management
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>("donations");

  const { toast } = useToast();

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    
    const hasAccess = isAdmin || isViewer || isBlogEditor || isBibleSchoolEditor || isAnnouncementEditor || isSermonEditor || isMusicEditor;
    if (!hasAccess) {
      toast({
        title: t("admin.error"),
        description: t("admin.accessdenied"),
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    // Set active tab based on URL parameter or user role
    const tabParam = searchParams.get('tab');
    const actionParam = searchParams.get('action');
    
    if (tabParam === 'blog' && (isBlogEditor || isAdmin)) {
      setActiveTab('blog');
      fetchBlogPosts();
      if (actionParam === 'add') {
        setTimeout(() => setIsBlogDialogOpen(true), 300);
      }
    } else if (tabParam === 'courses' && (isBibleSchoolEditor || isAdmin)) {
      setActiveTab('bibleschool');
      fetchCourses();
      if (actionParam === 'add') {
        setTimeout(() => setIsCourseDialogOpen(true), 300);
      }
    } else if (isAdmin || isViewer) {
      setActiveTab(tabParam || 'donations');
    } else if (isBlogEditor) {
      setActiveTab('blog');
      fetchBlogPosts();
    } else if (isBibleSchoolEditor) {
      setActiveTab('bibleschool');
      fetchCourses();
    }

    if (!tabParam || tabParam === 'donations') {
      fetchDonations();
    }
  }, [user, isAdmin, isViewer, isBlogEditor, isBibleSchoolEditor, navigate, searchParams]);

  useEffect(() => {
    if (selectedMission) {
      fetchMissionTrips();
      setSelectedTrip(null);
      setMissionPhotos([]);
    }
  }, [selectedMission]);

  useEffect(() => {
    if (activeTab === 'content') {
      fetchSiteContent();
    }
  }, [selectedContentSection, language, activeTab]);

  // Real-time updates for sermons
  useEffect(() => {
    const sermonsChannel = supabase
      .channel('admin-sermons-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sermons'
        },
        () => {
          if (isAdmin || isViewer || isSermonEditor) {
            fetchSermons();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sermons_translations'
        },
        () => {
          if (isAdmin || isViewer || isSermonEditor) {
            fetchSermons();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sermonsChannel);
    };
  }, [isAdmin, isViewer, isSermonEditor]);

  const fetchDonations = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('donations')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: t("admin.error"),
        description: t("admin.donations.fetcherror"),
        variant: "destructive",
      });
    } else {
      setDonations(data || []);
      setFilteredDonations(data || []);
    }
    setIsLoading(false);
  };

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-manage-users', {
        body: { action: 'list' }
      });

      if (error) throw error;

      const usersList = data.users || [];
      
      // Fetch all user roles using secure function
      const { data: rolesData, error: rolesError } = await supabase
        .rpc('get_all_user_roles');

      if (rolesError) throw rolesError;

      // Group roles by user_id with is_primary flag
      const rolesByUser = new Map<string, { role: string; is_primary?: boolean }[]>();
      (rolesData || []).forEach(r => {
        const roles = rolesByUser.get(r.user_id) || [];
        roles.push({ role: r.role, is_primary: r.is_primary });
        rolesByUser.set(r.user_id, roles);
      });

      const adminsCount = Array.from(rolesByUser.values()).filter(roles => 
        roles.some(r => r.role === 'admin')
      ).length;
      setAdminCount(adminsCount);

      // Check if current user is primary admin
      const currentUserRoles = rolesByUser.get(user?.id || '');
      const isCurrentUserPrimaryAdmin = currentUserRoles?.some(r => r.role === 'admin' && r.is_primary) || false;
      setIsPrimaryAdmin(isCurrentUserPrimaryAdmin);
      
      // Add roles to users
      const usersWithRoles = usersList.map((user: AppUser) => ({
        ...user,
        roles: rolesByUser.get(user.id) || []
      }));
      
      // Sort users: primary admin first, then other admins, then viewers, then no role
      const sortedUsers = usersWithRoles.sort((a, b) => {
        const aIsPrimaryAdmin = a.roles.some(r => r.role === 'admin' && r.is_primary);
        const bIsPrimaryAdmin = b.roles.some(r => r.role === 'admin' && r.is_primary);
        const aIsAdmin = a.roles.some(r => r.role === 'admin');
        const bIsAdmin = b.roles.some(r => r.role === 'admin');
        const aIsViewer = a.roles.some(r => r.role === 'viewer');
        const bIsViewer = b.roles.some(r => r.role === 'viewer');
        
        if (aIsPrimaryAdmin && !bIsPrimaryAdmin) return -1;
        if (!aIsPrimaryAdmin && bIsPrimaryAdmin) return 1;
        if (aIsAdmin && !bIsAdmin) return -1;
        if (!aIsAdmin && bIsAdmin) return 1;
        if (aIsViewer && !bIsViewer) return -1;
        if (!aIsViewer && bIsViewer) return 1;
        
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
      
      setUsers(sortedUsers);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.users.fetcherror"),
        variant: "destructive",
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!confirm(`${t("admin.users.deleteconfirm")} ${userEmail}?`)) {
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('admin-manage-users', {
        body: { action: 'delete', userId }
      });

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: t("admin.users.deletedsuccess"),
      });
      
      // Refresh the users list
      fetchUsers();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.users.deleteerror"),
        variant: "destructive",
      });
    }
  };

  const handleAssignRole = async (userId: string, role: 'admin' | 'viewer' | 'blog_editor' | 'bible_school_editor' | 'announcement_editor' | 'sermon_editor' | 'member' | 'music_editor' | 'music_viewer') => {
    // Special handling for member role - open member registration form
    if (role === 'member') {
      const userData = users.find(u => u.id === userId);
      setMemberFormData({
        user_id: userId,
        full_name: "",
        email: userData?.email || "",
        phone: "",
        address: "",
        number_of_kids: 0,
        date_of_birth: "",
      });
      setIsMemberFormDialogOpen(true);
      return;
    }

    try {
      const { error } = await supabase.rpc('assign_user_role', {
        _user_id: userId,
        _role: role
      });

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: t("admin.users.roleassigned"),
      });
      
      fetchUsers();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.users.roleerror"),
        variant: "destructive",
      });
    }
  };

  const handleRemoveRole = async (userId: string, role: 'admin' | 'viewer' | 'blog_editor' | 'bible_school_editor' | 'announcement_editor' | 'sermon_editor' | 'member' | 'music_editor' | 'music_viewer') => {
    // Special handling for member role - also delete member record
    if (role === 'member') {
      try {
        // First, delete the member record
        const { error: memberError } = await supabase
          .from('members')
          .delete()
          .eq('user_id', userId);

        if (memberError) throw memberError;

        // Role will be auto-removed by cascade
        toast({
          title: t("admin.success"),
          description: "Member role and record removed",
        });
        
        fetchUsers();
      } catch (error: any) {
        toast({
          title: t("admin.error"),
          description: error.message || t("admin.users.roleerror"),
          variant: "destructive",
        });
      }
      return;
    }

    try {
      const { error } = await supabase.rpc('remove_user_role', {
        _user_id: userId,
        _role: role
      });

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: t("admin.users.roleremoved"),
      });
      
      fetchUsers();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.users.roleerror"),
        variant: "destructive",
      });
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Special handling for member role - open member form after creating user
    if (createUserForm.role === 'member') {
      try {
        const requestBody: any = {
          action: 'create',
          email: createUserForm.email,
          password: createUserForm.password,
          // Don't assign role yet - will be auto-assigned when member record is created
        };

        const { data, error } = await supabase.functions.invoke('admin-manage-users', {
          body: requestBody
        });

        if (error) throw error;

        // Get the created user ID
        const newUserId = data.user.id;

        toast({
          title: t("admin.success"),
          description: t("admin.users.createsuccess"),
        });

        setIsCreateUserDialogOpen(false);
        
        // Open member form dialog
        setMemberFormData({
          user_id: newUserId,
          full_name: "",
          email: createUserForm.email,
          phone: "",
          address: "",
          number_of_kids: 0,
          date_of_birth: "",
        });
        setIsMemberFormDialogOpen(true);
        
        setCreateUserForm({
          email: "",
          password: "",
          role: "none"
        });
      } catch (error: any) {
        toast({
          title: t("admin.error"),
          description: error.message || t("admin.users.createerror"),
          variant: "destructive",
        });
      }
      return;
    }

    try {
      const requestBody: any = {
        action: 'create',
        email: createUserForm.email,
        password: createUserForm.password
      };

      if (createUserForm.role !== 'none') {
        requestBody.role = createUserForm.role;
      }

      const { data, error } = await supabase.functions.invoke('admin-manage-users', {
        body: requestBody
      });

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: t("admin.users.createsuccess"),
      });

      setIsCreateUserDialogOpen(false);
      setCreateUserForm({
        email: "",
        password: "",
        role: "none"
      });
      
      fetchUsers();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.users.createerror"),
        variant: "destructive",
      });
    }
  };

  const handleSubmitMemberForm = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!memberFormData.full_name || !memberFormData.email) {
        throw new Error("Full name and email are required");
      }

      // Insert member record - role will be auto-assigned by database trigger
      const { error } = await supabase.from("members").insert({
        user_id: memberFormData.user_id,
        full_name: memberFormData.full_name,
        email: memberFormData.email,
        phone: memberFormData.phone || null,
        address: memberFormData.address || null,
        number_of_kids: memberFormData.number_of_kids,
        date_of_birth: memberFormData.date_of_birth || null,
        membership_status: "active", // Admin-created members are auto-approved
      });

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: "Member registration completed successfully",
      });

      setIsMemberFormDialogOpen(false);
      setMemberFormData({
        user_id: "",
        full_name: "",
        email: "",
        phone: "",
        address: "",
        number_of_kids: 0,
        date_of_birth: "",
      });
      
      fetchUsers();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to register member",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (donationId: string) => {
    const { error } = await supabase
      .from('donations')
      .delete()
      .eq('id', donationId);

    if (error) {
      toast({
        title: t("admin.error"),
        description: t("admin.donations.deleteerror"),
        variant: "destructive",
      });
    } else {
      toast({
        title: t("admin.success"),
        description: t("admin.donations.deletedsuccess"),
      });
      // Update the local state to remove the deleted donation
      const updatedDonations = donations.filter(d => d.id !== donationId);
      setDonations(updatedDonations);
      setFilteredDonations(updatedDonations.filter(d => 
        selectedCategory === "all" || d.category === selectedCategory
      ));
    }
  };

  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredDonations(donations);
    } else {
      setFilteredDonations(donations.filter(d => d.category === selectedCategory));
    }
  }, [selectedCategory, donations]);

  const getCategoryName = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      guatemala: t("admin.category.guatemala"),
      mexico: t("admin.category.mexico"),
      cuba: t("admin.category.cuba"),
      church: t("admin.category.church"),
      tithe: t("admin.category.tithe")
    };
    return categoryMap[category] || category;
  };

  const getTotalByCategory = () => {
    const totals: { [key: string]: number } = {};
    donations.forEach(donation => {
      if (!totals[donation.category]) {
        totals[donation.category] = 0;
      }
      totals[donation.category] += Number(donation.amount);
    });
    return totals;
  };

  const fetchSermons = async () => {
    setIsLoadingSermons(true);
    try {
      let query = supabase
        .from("sermons")
        .select("*");
      
      // Filter by role: sermon editors can only see their own content
      if (isSermonEditor && !isAdmin) {
        query = query.eq('created_by', user?.id);
      }
      
      const { data: sermonsData, error: sermonsError } = await query.order("sermon_date", { ascending: false });

      if (sermonsError) throw sermonsError;

      // Fetch all translations
      const { data: translationsData, error: translationsError } = await supabase
        .from("sermons_translations")
        .select("*");

      if (translationsError) throw translationsError;

      // Map translations to sermons
      const sermonsWithTranslations = (sermonsData || []).map(sermon => {
        const sermonTranslations: Record<string, SermonTranslation> = {};
        (translationsData || [])
          .filter(t => t.sermon_id === sermon.id)
          .forEach(t => {
            sermonTranslations[t.language] = t;
          });
        return {
          ...sermon,
          translations: sermonTranslations
        };
      });

      setSermons(sermonsWithTranslations);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: t("admin.sermons.fetcherror"),
        variant: "destructive",
      });
    } finally {
      setIsLoadingSermons(false);
    }
  };

  const getSermonDisplayText = (sermon: Sermon, field: keyof Sermon) => {
    if (sermon.translations && sermon.translations[language]) {
      const translation = sermon.translations[language];
      if (field === 'title') return translation.title || sermon.title;
      if (field === 'speaker') return translation.speaker || sermon.speaker;
      if (field === 'scripture_reference') return translation.scripture_reference || sermon.scripture_reference;
    }
    return sermon[field] as string;
  };

  const handleSermonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Submitting sermon with scripture_reference:', sermonForm.scripture_reference);
    setSermonSavingProgress(10);
    
    try {
      let sermonId = editingSermon?.id;
      
      if (editingSermon) {
        console.log('Updating sermon ID:', editingSermon.id);
        setSermonSavingProgress(20);
        
        const { data, error } = await supabase
          .from("sermons")
          .update({
            title: sermonForm.title,
            description: sermonForm.description || null,
            sermon_date: sermonForm.sermon_date,
            speaker: sermonForm.speaker,
            audio_url: sermonForm.audio_url || null,
            video_url: sermonForm.video_url || null,
            scripture_reference: sermonForm.scripture_reference || null,
            published: sermonForm.published,
          })
          .eq("id", editingSermon.id)
          .select();

        if (error) throw error;
        console.log('Update result:', data);
        setSermonSavingProgress(30);
      } else {
        setSermonSavingProgress(20);
        
        const { data, error } = await supabase
          .from("sermons")
          .insert({
            title: sermonForm.title,
            description: sermonForm.description || null,
            sermon_date: sermonForm.sermon_date,
            speaker: sermonForm.speaker,
            audio_url: sermonForm.audio_url || null,
            video_url: sermonForm.video_url || null,
            scripture_reference: sermonForm.scripture_reference || null,
            published: sermonForm.published,
            created_by: user?.id,
          })
          .select();

        if (error) throw error;
        sermonId = data[0].id;
        setSermonSavingProgress(30);
      }
      
      // Save the current language version first (no translation needed)
      setSermonSavingProgress(40);
      await supabase
        .from("sermons_translations")
        .upsert({
          sermon_id: sermonId,
          language: language,
          title: sermonForm.title,
          description: sermonForm.description || null,
          scripture_reference: sermonForm.scripture_reference || null,
          speaker: sermonForm.speaker,
        }, {
          onConflict: 'sermon_id,language'
        });
      
      // Auto-translate to OTHER languages only (exclude current language)
      const targetLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
      
      if (targetLanguages.length > 0) {
        try {
          setSermonSavingProgress(50);
          
          // Translate title
          const { data: titleTranslations, error: titleError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: sermonForm.title,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setSermonSavingProgress(60);
          
          // Translate description
          const { data: descTranslations, error: descError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: sermonForm.description || '',
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setSermonSavingProgress(70);
          
          // Translate speaker
          const { data: speakerTranslations, error: speakerError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: sermonForm.speaker,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setSermonSavingProgress(75);
          
          // Translate scripture reference
          const { data: scriptureTranslations, error: scriptureError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: sermonForm.scripture_reference || '',
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setSermonSavingProgress(80);
          
          if (!titleError && !descError && !speakerError && !scriptureError &&
              titleTranslations?.translations && descTranslations?.translations && 
              speakerTranslations?.translations && scriptureTranslations?.translations) {
            
            // Save translations for each target language
            for (const lang of targetLanguages) {
              await supabase
                .from("sermons_translations")
                .upsert({
                  sermon_id: sermonId,
                  language: lang,
                  title: titleTranslations.translations[lang] || sermonForm.title,
                  description: descTranslations.translations[lang] || sermonForm.description,
                  speaker: speakerTranslations.translations[lang] || sermonForm.speaker,
                  scripture_reference: scriptureTranslations.translations[lang] || sermonForm.scripture_reference,
                }, {
                  onConflict: 'sermon_id,language'
                });
            }
          }
        } catch (error) {
          console.error('Translation error:', error);
        }
      }
      
      setSermonSavingProgress(90);
      
      toast({
        title: t("admin.success"),
        description: editingSermon ? t("admin.sermons.updatedsuccess") : t("admin.sermons.addedsuccess"),
      });

      setIsSermonDialogOpen(false);
      setEditingSermon(null);
      setSermonForm({
        title: "",
        description: "",
        sermon_date: "",
        speaker: "",
        audio_url: "",
        video_url: "",
        scripture_reference: "",
        published: false,
      });
      
      setSermonSavingProgress(100);
      setTimeout(() => setSermonSavingProgress(0), 500);
      
      await fetchSermons();
    } catch (error: any) {
      console.error('Error updating sermon:', error);
      setSermonSavingProgress(0);
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.sermons.saveerror"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteSermon = async (sermonId: string) => {
    if (!confirm(t("admin.sermons.deleteconfirm"))) {
      return;
    }

    try {
      const { error, count } = await supabase
        .from("sermons")
        .delete({ count: 'exact' })
        .eq("id", sermonId);

      if (error) throw error;

      if (count === 0) {
        toast({
          title: t("admin.error"),
          description: "Cannot delete this sermon. You can only delete your own draft sermons.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: t("admin.success"),
        description: t("admin.sermons.deletedsuccess"),
      });

      fetchSermons();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.sermons.deleteerror"),
        variant: "destructive",
      });
    }
  };

  const handleEditSermon = (sermon: Sermon) => {
    setEditingSermon(sermon);
    
    // Load the translation for the current language if it exists
    const translation = sermon.translations?.[language];
    
    setSermonForm({
      title: translation?.title || sermon.title,
      description: translation?.description || sermon.description || "",
      sermon_date: sermon.sermon_date,
      speaker: translation?.speaker || sermon.speaker,
      audio_url: sermon.audio_url || "",
      video_url: sermon.video_url || "",
      scripture_reference: translation?.scripture_reference || sermon.scripture_reference || "",
      published: sermon.published || false,
    });
    setIsSermonDialogOpen(true);
  };

  const handleCloseSermonDialog = () => {
    setIsSermonDialogOpen(false);
    setEditingSermon(null);
    setSermonForm({
      title: "",
      description: "",
      sermon_date: "",
      speaker: "",
      audio_url: "",
      video_url: "",
      scripture_reference: "",
      published: false,
    });
  };

  const handleManageTranslations = async (sermon: Sermon) => {
    setTranslatingSermon(sermon);
    setIsTranslationsDialogOpen(true);
    
    // Fetch existing translations
    try {
      const { data, error } = await supabase
        .from("sermons_translations")
        .select("*")
        .eq("sermon_id", sermon.id);
      
      if (error) throw error;
      
      const translationsMap: Record<string, SermonTranslation> = {};
      if (data) {
        data.forEach(t => {
          translationsMap[t.language] = t;
        });
      }
      
      // Initialize with empty translations for missing languages
      ['en', 'ru', 'es'].forEach(lang => {
        if (!translationsMap[lang]) {
          translationsMap[lang] = {
            sermon_id: sermon.id,
            language: lang,
            title: sermon.title,
            description: sermon.description || "",
            scripture_reference: sermon.scripture_reference || "",
            speaker: sermon.speaker,
          };
        }
      });
      
      setTranslations(translationsMap);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSaveTranslations = async () => {
    if (!translatingSermon) return;
    
    try {
      // Upsert translations for all languages
      for (const lang of ['en', 'ru', 'es']) {
        const translation = translations[lang];
        if (translation && translation.title && translation.speaker) {
          const { error } = await supabase
            .from("sermons_translations")
            .upsert({
              sermon_id: translatingSermon.id,
              language: lang,
              title: translation.title,
              description: translation.description || null,
              scripture_reference: translation.scripture_reference || null,
              speaker: translation.speaker,
            }, {
              onConflict: 'sermon_id,language'
            });
          
          if (error) throw error;
        }
      }
      
      toast({
        title: t("admin.success"),
        description: t("admin.sermons.translations.success"),
      });
      
      setIsTranslationsDialogOpen(false);
      setTranslatingSermon(null);
      setTranslations({});
      
      // Force refresh of sermons data to show updated translations
      await fetchSermons();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.sermons.translations.error"),
        variant: "destructive",
      });
    }
  };

  const updateTranslation = (lang: string, field: string, value: string) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value,
      }
    }));
  };

  const fetchMissionPhotos = async () => {
    setIsLoadingPhotos(true);
    try {
      const { data, error } = await supabase
        .from('mission_photos')
        .select('*')
        .eq('mission', selectedMission)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setMissionPhotos(data || []);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: t("admin.photos.fetcherror"),
        variant: "destructive",
      });
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const handlePhotoUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTrip) {
      toast({
        title: t("admin.error"),
        description: "Please select a trip first",
        variant: "destructive",
      });
      return;
    }

    // Handle YouTube video URL
    if (photoForm.video_url) {
      if (!photoForm.title) {
        toast({
          title: t("admin.error"),
          description: "Title is required for YouTube videos",
          variant: "destructive",
        });
        return;
      }

      try {
        const { error } = await supabase
          .from('mission_photos')
          .insert({
            trip_id: selectedTrip,
            mission: selectedMission,
            title: photoForm.title,
            description: photoForm.description || null,
            image_url: '', // Empty for YouTube videos
            video_url: photoForm.video_url,
            media_type: 'video',
            display_order: photoForm.display_order,
          });

        if (error) throw error;

        toast({
          title: t("admin.success"),
          description: "YouTube video added successfully",
        });

        setIsPhotoDialogOpen(false);
        setPhotoForm({ title: "", description: "", display_order: 0, video_url: "" });
        fetchTripPhotos(selectedTrip);
      } catch (error: any) {
        toast({
          title: t("admin.error"),
          description: error.message || "Failed to add YouTube video",
          variant: "destructive",
        });
      }
      return;
    }
    
    if (uploadingFiles.length === 0) {
      toast({
        title: t("admin.error"),
        description: t("admin.photos.nofile"),
        variant: "destructive",
      });
      return;
    }

    try {
      let successCount = 0;
      setUploadProgress(0);
      
      // Upload each file
      for (let i = 0; i < uploadingFiles.length; i++) {
        const file = uploadingFiles[i];
        setCurrentUploadingFile(file.name);
        
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        const fileName = `${selectedMission}/${Date.now()}_${i}.${fileExt}`;
        
        // Determine if it's a video or image
        const isVideo = ['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(fileExt || '');
        
        // Upload file to storage
        const { error: uploadError } = await supabase.storage
          .from('mission-photos')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Save media metadata with file path (not public URL - bucket is private)
        const { error: insertError } = await supabase
          .from('mission_photos')
          .insert({
            trip_id: selectedTrip,
            mission: selectedMission,
            title: photoForm.title || file.name,
            description: photoForm.description || null,
            image_url: isVideo ? '' : fileName,
            video_url: isVideo ? fileName : null,
            media_type: isVideo ? 'video' : 'photo',
            display_order: photoForm.display_order + i,
          });

        if (insertError) throw insertError;
        successCount++;
        
        // Update progress
        const progress = ((i + 1) / uploadingFiles.length) * 100;
        setUploadProgress(progress);
      }

      toast({
        title: t("admin.success"),
        description: `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully`,
      });

      setIsPhotoDialogOpen(false);
      setPhotoForm({ title: "", description: "", display_order: 0, video_url: "" });
      setUploadingFiles([]);
      setUploadProgress(0);
      setCurrentUploadingFile("");
      fetchTripPhotos(selectedTrip);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.photos.uploaderror"),
        variant: "destructive",
      });
      setUploadProgress(0);
      setCurrentUploadingFile("");
    }
  };

  const handleEditPhoto = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingPhoto || !selectedTrip) return;

    try {
      const { error } = await supabase
        .from('mission_photos')
        .update({
          title: photoForm.title,
          description: photoForm.description || null,
          display_order: photoForm.display_order,
        })
        .eq('id', editingPhoto.id);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: t("admin.photos.updatesuccess"),
      });

      setIsPhotoDialogOpen(false);
      setEditingPhoto(null);
      setPhotoForm({ title: "", description: "", display_order: 0, video_url: "" });
      fetchTripPhotos(selectedTrip);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.photos.updateerror"),
        variant: "destructive",
      });
    }
  };

  const handleDeletePhoto = async (photoId: string, imageUrl: string) => {
    if (!confirm(t("admin.photos.deleteconfirm"))) return;

    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/mission-photos/');
      if (urlParts.length > 1) {
        const filePath = urlParts[1].split('?')[0];
        
        // Delete from storage
        await supabase.storage
          .from('mission-photos')
          .remove([filePath]);
      }

      // Delete from database
      const { error } = await supabase
        .from('mission_photos')
        .delete()
        .eq('id', photoId);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: t("admin.photos.deletesuccess"),
      });

      if (selectedTrip) {
        fetchTripPhotos(selectedTrip);
      }
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.photos.deleteerror"),
        variant: "destructive",
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPhotos.length === 0) return;
    if (!confirm(`Delete ${selectedPhotos.length} selected item${selectedPhotos.length > 1 ? 's' : ''}?`)) return;
    
    try {
      // Get photo details for storage deletion
      const photosToDelete = missionPhotos.filter(p => selectedPhotos.includes(p.id));
      
      // Delete from storage
      for (const photo of photosToDelete) {
        const urlParts = photo.image_url.split('/mission-photos/');
        if (urlParts.length > 1) {
          const filePath = urlParts[1].split('?')[0];
          await supabase.storage
            .from('mission-photos')
            .remove([filePath]);
        }
      }

      // Delete from database
      const { error } = await supabase
        .from('mission_photos')
        .delete()
        .in('id', selectedPhotos);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: `${selectedPhotos.length} item${selectedPhotos.length > 1 ? 's' : ''} deleted successfully`,
      });

      setSelectedPhotos([]);
      setIsSelectMode(false);
      if (selectedTrip) {
        fetchTripPhotos(selectedTrip);
      }
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete items",
        variant: "destructive",
      });
    }
  };

  const togglePhotoSelection = (id: string) => {
    setSelectedPhotos(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const selectAllPhotos = () => {
    setSelectedPhotos(missionPhotos.map(p => p.id));
  };

  const deselectAllPhotos = () => {
    setSelectedPhotos([]);
  };

  // Mission Trips Functions
  const fetchMissionTrips = async () => {
    setIsLoadingTrips(true);
    try {
      const { data, error } = await supabase
        .from('mission_trips')
        .select('*')
        .eq('mission', selectedMission)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setMissionTrips(data || []);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: "Failed to fetch mission trips",
        variant: "destructive",
      });
    } finally {
      setIsLoadingTrips(false);
    }
  };

  const fetchTripPhotos = async (tripId: string) => {
    setIsLoadingPhotos(true);
    try {
      const { data, error } = await supabase
        .from('mission_photos')
        .select('*')
        .eq('trip_id', tripId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setMissionPhotos(data || []);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: t("admin.photos.fetcherror"),
        variant: "destructive",
      });
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const openTripDialog = (trip?: MissionTrip) => {
    if (trip) {
      setEditingTrip(trip);
      setTripForm({
        title: trip.title,
        description: trip.description || "",
        trip_date: trip.trip_date || "",
        display_order: trip.display_order,
      });
    } else {
      setEditingTrip(null);
      setTripForm({
        title: "",
        description: "",
        trip_date: "",
        display_order: 0,
      });
    }
    setIsTripDialogOpen(true);
  };

  const closeTripDialog = () => {
    setIsTripDialogOpen(false);
    setEditingTrip(null);
    setTripForm({
      title: "",
      description: "",
      trip_date: "",
      display_order: 0,
    });
  };

  const handleTripSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingTrip) {
        const { error } = await supabase
          .from('mission_trips')
          .update({
            title: tripForm.title,
            description: tripForm.description || null,
            trip_date: tripForm.trip_date || null,
            display_order: tripForm.display_order,
          })
          .eq('id', editingTrip.id);

        if (error) throw error;

        toast({
          title: t("admin.success"),
          description: "Trip updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('mission_trips')
          .insert({
            mission: selectedMission,
            title: tripForm.title,
            description: tripForm.description || null,
            trip_date: tripForm.trip_date || null,
            display_order: tripForm.display_order,
          });

        if (error) throw error;

        toast({
          title: t("admin.success"),
          description: "Trip created successfully",
        });
      }

      closeTripDialog();
      fetchMissionTrips();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to save trip",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTrip = async (tripId: string) => {
    if (!confirm("Delete this trip and all its photos?")) return;

    try {
      const { error } = await supabase
        .from('mission_trips')
        .delete()
        .eq('id', tripId);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: "Trip deleted successfully",
      });

      if (selectedTrip === tripId) {
        setSelectedTrip(null);
        setMissionPhotos([]);
      }
      fetchMissionTrips();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete trip",
        variant: "destructive",
      });
    }
  };

  // Special Events Functions
  const fetchSpecialEvents = async () => {
    setIsLoadingEvents(true);
    try {
      const { data, error } = await supabase
        .from('special_events')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setSpecialEvents(data || []);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: "Failed to fetch special events",
        variant: "destructive",
      });
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const fetchEventMedia = async (eventId: string) => {
    setIsLoadingMedia(true);
    try {
      const { data, error } = await supabase
        .from('special_event_media')
        .select('*')
        .eq('event_id', eventId)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setEventMedia(data || []);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: "Failed to fetch event media",
        variant: "destructive",
      });
    } finally {
      setIsLoadingMedia(false);
    }
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingEvent) {
        const { error } = await supabase
          .from('special_events')
          .update({
            title: eventForm.title,
            description: eventForm.description || null,
            event_date: eventForm.event_date || null,
            display_order: eventForm.display_order,
          })
          .eq('id', editingEvent.id);

        if (error) throw error;

        toast({
          title: t("admin.success"),
          description: "Event updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('special_events')
          .insert({
            title: eventForm.title,
            description: eventForm.description || null,
            event_date: eventForm.event_date || null,
            display_order: eventForm.display_order,
          });

        if (error) throw error;

        toast({
          title: t("admin.success"),
          description: "Event created successfully",
        });
      }

      setIsEventDialogOpen(false);
      setEditingEvent(null);
      setEventForm({ title: "", description: "", event_date: "", display_order: 0 });
      fetchSpecialEvents();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to save event",
        variant: "destructive",
      });
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm("Are you sure you want to delete this event and all its media?")) return;

    try {
      // Media will be deleted automatically due to CASCADE
      const { error } = await supabase
        .from('special_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: "Event deleted successfully",
      });

      if (selectedEvent === eventId) {
        setSelectedEvent(null);
        setEventMedia([]);
      }
      fetchSpecialEvents();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete event",
        variant: "destructive",
      });
    }
  };

  const handleMediaUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEvent) {
      toast({
        title: t("admin.error"),
        description: "Please select an event first",
        variant: "destructive",
      });
      return;
    }

    // Check if YouTube URL is provided
    if (mediaForm.video_url) {
      // YouTube video - optional thumbnail upload
      try {
        let imageUrl = "";
        
        if (uploadingFiles.length > 0) {
          const file = uploadingFiles[0];
          const fileExt = file.name.split('.').pop()?.toLowerCase();
          const fileName = `special-events/${selectedEvent}/${Date.now()}_thumbnail.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('mission-photos')
            .upload(fileName, file);

          if (uploadError) throw uploadError;

          // Store file path (not public URL - bucket is private)
          imageUrl = fileName;
        }

        const { error: insertError } = await supabase
          .from('special_event_media')
          .insert({
            event_id: selectedEvent,
            title: mediaForm.title,
            description: mediaForm.description || null,
            image_url: imageUrl,
            video_url: mediaForm.video_url,
            media_type: 'video',
            display_order: mediaForm.display_order,
          });

        if (insertError) throw insertError;

        toast({
          title: t("admin.success"),
          description: "YouTube video added successfully",
        });

        setIsMediaDialogOpen(false);
        setMediaForm({ title: "", description: "", display_order: 0, video_url: "" });
        setUploadingFiles([]);
        fetchEventMedia(selectedEvent);
      } catch (error: any) {
        toast({
          title: t("admin.error"),
          description: error.message || "Failed to add YouTube video",
          variant: "destructive",
        });
      }
      return;
    }

    // Regular file upload
    if (uploadingFiles.length === 0) {
      toast({
        title: t("admin.error"),
        description: "Please select at least one file or provide a YouTube URL",
        variant: "destructive",
      });
      return;
    }

    try {
      let successCount = 0;
      setUploadProgress(0);
      
      for (let i = 0; i < uploadingFiles.length; i++) {
        const file = uploadingFiles[i];
        setCurrentUploadingFile(file.name);
        
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        const fileName = `special-events/${selectedEvent}/${Date.now()}_${i}.${fileExt}`;
        
        const isVideo = ['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(fileExt || '');
        
        const { error: uploadError } = await supabase.storage
          .from('mission-photos')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Save media metadata with file path (not public URL - bucket is private)
        const { error: insertError } = await supabase
          .from('special_event_media')
          .insert({
            event_id: selectedEvent,
            title: mediaForm.title || file.name,
            description: mediaForm.description || null,
            image_url: fileName,
            video_url: isVideo ? fileName : null,
            media_type: isVideo ? 'video' : 'photo',
            display_order: mediaForm.display_order + i,
          });

        if (insertError) throw insertError;
        successCount++;
        
        const progress = ((i + 1) / uploadingFiles.length) * 100;
        setUploadProgress(progress);
      }

      toast({
        title: t("admin.success"),
        description: `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully`,
      });

      setIsMediaDialogOpen(false);
      setMediaForm({ title: "", description: "", display_order: 0, video_url: "" });
      setUploadingFiles([]);
      setUploadProgress(0);
      setCurrentUploadingFile("");
      fetchEventMedia(selectedEvent);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to upload",
        variant: "destructive",
      });
      setUploadProgress(0);
      setCurrentUploadingFile("");
    }
  };

  const handleEditMedia = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingMedia) return;

    try {
      const { error } = await supabase
        .from('special_event_media')
        .update({
          title: mediaForm.title,
          description: mediaForm.description || null,
          display_order: mediaForm.display_order,
        })
        .eq('id', editingMedia.id);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: "Media updated successfully",
      });

      setIsMediaDialogOpen(false);
      setEditingMedia(null);
      setMediaForm({ title: "", description: "", display_order: 0, video_url: "" });
      if (selectedEvent) fetchEventMedia(selectedEvent);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to update media",
        variant: "destructive",
      });
    }
  };

  const handleDeleteMedia = async (mediaId: string, imageUrl: string) => {
    if (!confirm("Are you sure you want to delete this media?")) return;

    try {
      const urlParts = imageUrl.split('/mission-photos/');
      if (urlParts.length > 1) {
        const filePath = urlParts[1].split('?')[0];
        
        await supabase.storage
          .from('mission-photos')
          .remove([filePath]);
      }

      const { error } = await supabase
        .from('special_event_media')
        .delete()
        .eq('id', mediaId);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: "Media deleted successfully",
      });

      if (selectedEvent) fetchEventMedia(selectedEvent);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete media",
        variant: "destructive",
      });
    }
  };

  const handleBulkDeleteMedia = async () => {
    if (selectedMedia.length === 0) return;
    if (!confirm(`Delete ${selectedMedia.length} selected item${selectedMedia.length > 1 ? 's' : ''}?`)) return;
    
    try {
      const mediaToDelete = eventMedia.filter(m => selectedMedia.includes(m.id));
      
      for (const media of mediaToDelete) {
        const urlParts = media.image_url.split('/mission-photos/');
        if (urlParts.length > 1) {
          const filePath = urlParts[1].split('?')[0];
          await supabase.storage
            .from('mission-photos')
            .remove([filePath]);
        }
      }

      const { error } = await supabase
        .from('special_event_media')
        .delete()
        .in('id', selectedMedia);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: `${selectedMedia.length} item${selectedMedia.length > 1 ? 's' : ''} deleted successfully`,
      });

      setSelectedMedia([]);
      setIsMediaSelectMode(false);
      if (selectedEvent) fetchEventMedia(selectedEvent);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete media",
        variant: "destructive",
      });
    }
  };

  const openEventDialog = (event?: SpecialEvent) => {
    if (event) {
      setEditingEvent(event);
      setEventForm({
        title: event.title,
        description: event.description || "",
        event_date: event.event_date || "",
        display_order: event.display_order,
      });
    } else {
      setEditingEvent(null);
      setEventForm({ title: "", description: "", event_date: "", display_order: 0 });
    }
    setIsEventDialogOpen(true);
  };

  const closeEventDialog = () => {
    setIsEventDialogOpen(false);
    setEditingEvent(null);
    setEventForm({ title: "", description: "", event_date: "", display_order: 0 });
  };

  const openMediaDialog = (media?: SpecialEventMedia) => {
    if (media) {
      setEditingMedia(media);
      setMediaForm({
        title: media.title,
        description: media.description || "",
        display_order: media.display_order,
        video_url: "",
      });
    } else {
      setEditingMedia(null);
      setMediaForm({ title: "", description: "", display_order: 0, video_url: "" });
      setUploadingFiles([]);
    }
    setIsMediaDialogOpen(true);
  };

  const closeMediaDialog = () => {
    setIsMediaDialogOpen(false);
    setEditingMedia(null);
    setMediaForm({ title: "", description: "", display_order: 0, video_url: "" });
    setUploadingFiles([]);
    setUploadProgress(0);
    setCurrentUploadingFile("");
  };

  const toggleMediaSelection = (mediaId: string) => {
    setSelectedMedia(prev =>
      prev.includes(mediaId)
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const selectAllMedia = () => {
    setSelectedMedia(eventMedia.map(m => m.id));
  };

  const deselectAllMedia = () => {
    setSelectedMedia([]);
  };

  const openPhotoDialog = (photo?: MissionPhoto) => {
    if (photo) {
      setEditingPhoto(photo);
      setPhotoForm({
        title: photo.title,
        description: photo.description || "",
        display_order: photo.display_order,
        video_url: photo.video_url || "",
      });
    } else {
      setEditingPhoto(null);
      setPhotoForm({ title: "", description: "", display_order: 0, video_url: "" });
      setUploadingFiles([]);
    }
    setIsPhotoDialogOpen(true);
  };

  // Contributor Requests functions
  const fetchContributorRequests = async () => {
    if (!isAdmin) return;
    
    setIsLoadingRequests(true);
    try {
      const { data, error } = await supabase
        .from('contributor_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContributorRequests(data || []);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to fetch contributor requests",
        variant: "destructive",
      });
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const handleApproveRequest = async (request: ContributorRequest) => {
    if (!isAdmin) return;

    const name = request.full_name;
    const email = request.email;
    const role = request.requested_role === 'both' ? 'blog_editor' : request.requested_role;
    const password = Math.random().toString(36).slice(-12) + Math.random().toString(36).slice(-12).toUpperCase() + '!@#';

    try {
      // Check if user already exists
      const { data: existingUsers } = await supabase.functions.invoke('admin-manage-users', {
        body: { action: 'list' }
      });

      const userExists = existingUsers?.users?.some((u: any) => u.email === email);
      let userId: string;

      if (userExists) {
        // User exists, just assign role
        console.log("User already exists, assigning role only");
        const existingUser = existingUsers.users.find((u: any) => u.email === email);
        userId = existingUser.id;

        // Assign the role
        await supabase.rpc('assign_user_role', {
          _user_id: userId,
          _role: role as "admin" | "blog_editor" | "bible_school_editor" | "announcement_editor" | "sermon_editor" | "moderator" | "user" | "viewer"
        });

        // If requested 'both', assign the second role as well
        if (request.requested_role === 'both') {
          await supabase.rpc('assign_user_role', {
            _user_id: userId,
            _role: 'bible_school_editor'
          });
        }

        // Send approval notification (no password needed, they already have one)
        try {
          await supabase.functions.invoke('send-contributor-notification', {
            body: {
              email,
              name,
              role: request.requested_role,
              type: 'approval',
              language: language
            }
          });
        } catch (notificationError) {
          console.error("Error sending approval notification:", notificationError);
        }

      } else {
        // User doesn't exist, create account
        console.log("Creating new user account");
        const { data: userData, error: createError } = await supabase.functions.invoke('admin-manage-users', {
          body: { 
            action: 'create', 
            email, 
            password,
            role 
          }
        });

        if (createError) throw createError;
        userId = userData.user.id;

        // If requested 'both', assign the second role as well
        if (request.requested_role === 'both') {
          await supabase.rpc('assign_user_role', {
            _user_id: userId,
            _role: 'bible_school_editor'
          });
        }

        // Send invitation email with credentials
        const { data: { session } } = await supabase.auth.getSession();
        const { error: emailError } = await supabase.functions.invoke('send-contributor-invitation', {
          body: {
            email,
            name,
            role: request.requested_role,
            password,
            language: language
          },
          headers: {
            Authorization: `Bearer ${session?.access_token}`
          }
        });

        if (emailError) {
          console.error("Error sending invitation email:", emailError);
        }

        // Send approval notification
        try {
          await supabase.functions.invoke('send-contributor-notification', {
            body: {
              email,
              name,
              role: request.requested_role,
              type: 'approval',
              language: language
            }
          });
        } catch (notificationError) {
          console.error("Error sending approval notification:", notificationError);
        }
      }

      // Update request status
      const { error: updateError } = await supabase
        .from('contributor_requests')
        .update({
          status: 'approved',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', request.id);

      if (updateError) throw updateError;

      toast({
        title: t("admin.success"),
        description: userExists 
          ? `${t("admin.contributors.approved")} (role assigned to existing user)`
          : t("admin.contributors.approved"),
      });

      await fetchContributorRequests();
    } catch (error: any) {
      console.error("Error approving request:", error);
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.contributors.approveerror"),
        variant: "destructive",
      });
    }
  };

  const handleRejectRequest = async (requestId: string, requestEmail: string, requestName: string, requestRole: string) => {
    if (!isAdmin) {
      console.error("User is not admin");
      return;
    }
    
    if (!confirm(t("admin.contributors.rejectconfirm"))) return;

    console.log("Rejecting request:", requestId);

    try {
      const { data, error } = await supabase
        .from('contributor_requests')
        .update({
          status: 'rejected',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', requestId)
        .select();

      console.log("Reject result:", { data, error });

      if (error) throw error;

      // Send rejection notification email
      try {
        await supabase.functions.invoke('send-contributor-notification', {
          body: {
            email: requestEmail,
            name: requestName,
            role: requestRole,
            type: 'rejection',
            language: language
          }
        });
      } catch (emailError) {
        console.error("Error sending rejection email:", emailError);
      }

      console.log("Request rejected successfully");
      
      toast({
        title: t("admin.success"),
        description: t("admin.contributors.rejected"),
      });

      await fetchContributorRequests();
    } catch (error: any) {
      console.error("Error rejecting request:", error);
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.contributors.rejecterror"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!isAdmin) return;
    if (!confirm(t("admin.contributors.deleteconfirm"))) return;

    try {
      const { error } = await supabase
        .from('contributor_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: t("admin.contributors.deleted"),
      });

      fetchContributorRequests();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || t("admin.contributors.deleteerror"),
        variant: "destructive",
      });
    }
  };

  const filteredRequests = selectedStatus === 'all' 
    ? contributorRequests 
    : contributorRequests.filter(r => r.status === selectedStatus);

  const getRoleName = (role: string) => {
    const roleMap: Record<string, string> = {
      'blog_editor': t("admin.contributors.roles.blogeditor"),
      'bible_school_editor': t("admin.contributors.roles.bibleschooleditor"),
      'both': t("admin.contributors.roles.both"),
    };
    return roleMap[role] || role;
  };

  const closePhotoDialog = () => {
    setIsPhotoDialogOpen(false);
    setEditingPhoto(null);
    setPhotoForm({ title: "", description: "", display_order: 0, video_url: "" });
    setUploadingFiles([]);
  };

  // Blog functions
  const fetchBlogPosts = async () => {
    setIsLoadingBlogs(true);
    try {
      // RLS policies now handle filtering by user for contributors
      const { data: blogData, error: blogError } = await supabase
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false });

      if (blogError) throw blogError;

      const { data: translationsData, error: translationsError } = await supabase
        .from("blog_posts_translations")
        .select("*");

      if (translationsError) throw translationsError;

      const blogsWithTranslations = (blogData || []).map(blog => {
        const blogTranslations: Record<string, BlogTranslation> = {};
        (translationsData || [])
          .filter(t => t.blog_post_id === blog.id)
          .forEach(t => {
            blogTranslations[t.language] = t;
          });
        return { ...blog, translations: blogTranslations };
      });

      setBlogPosts(blogsWithTranslations);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to fetch blog posts",
        variant: "destructive",
      });
    } finally {
      setIsLoadingBlogs(false);
    }
  };

  const handleBlogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBlogSavingProgress(10);
    
    try {
      const finalSlug = blogForm.slug || generateSlug(blogForm.title);
      let blogId = editingBlog?.id;
      
      if (editingBlog) {
        setBlogSavingProgress(20);
        
        const { error } = await supabase
          .from("blog_posts")
          .update({
            title: blogForm.title,
            slug: finalSlug,
            author: blogForm.author,
            category: blogForm.category,
            excerpt: blogForm.excerpt || null,
            content: blogForm.content,
            featured_image_url: blogForm.featured_image_url || null,
            published: blogForm.published,
            published_at: blogForm.published ? new Date().toISOString() : null,
          })
          .eq("id", editingBlog.id);

        if (error) throw error;
        setBlogSavingProgress(30);
      } else {
        setBlogSavingProgress(20);
        
        const { data, error } = await supabase
          .from("blog_posts")
          .insert({
            title: blogForm.title,
            slug: finalSlug,
            author: blogForm.author,
            category: blogForm.category,
            excerpt: blogForm.excerpt || null,
            content: blogForm.content,
            featured_image_url: blogForm.featured_image_url || null,
            published: blogForm.published,
            published_at: blogForm.published ? new Date().toISOString() : null,
            created_by: user?.id,
          })
          .select();

        if (error) throw error;
        blogId = data[0].id;
        setBlogSavingProgress(30);
      }
      
      // Save the current language version first (no translation needed)
      setBlogSavingProgress(40);
      await supabase
        .from("blog_posts_translations")
        .upsert({
          blog_post_id: blogId,
          language: language,
          title: blogForm.title,
          excerpt: blogForm.excerpt || null,
          content: blogForm.content,
          author: blogForm.author,
        }, {
          onConflict: 'blog_post_id,language'
        });
      
      // Auto-translate to OTHER languages only (exclude current language)
      const targetLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
      
      if (targetLanguages.length > 0) {
        try {
          setBlogSavingProgress(50);
          
          // Translate title
          const { data: titleTranslations, error: titleError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: blogForm.title,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setBlogSavingProgress(60);
          
          // Translate excerpt
          const { data: excerptTranslations, error: excerptError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: blogForm.excerpt || '',
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setBlogSavingProgress(65);
          
          // Translate content
          const { data: contentTranslations, error: contentError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: blogForm.content,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setBlogSavingProgress(75);
          
          // Translate author
          const { data: authorTranslations, error: authorError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: blogForm.author,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setBlogSavingProgress(80);
          
          if (!titleError && !excerptError && !contentError && !authorError &&
              titleTranslations?.translations && excerptTranslations?.translations && 
              contentTranslations?.translations && authorTranslations?.translations) {
            
            // Save translations for each target language
            for (const lang of targetLanguages) {
              await supabase
                .from("blog_posts_translations")
                .upsert({
                  blog_post_id: blogId,
                  language: lang,
                  title: titleTranslations.translations[lang] || blogForm.title,
                  excerpt: excerptTranslations.translations[lang] || blogForm.excerpt,
                  content: contentTranslations.translations[lang] || blogForm.content,
                  author: authorTranslations.translations[lang] || blogForm.author,
                }, {
                  onConflict: 'blog_post_id,language'
                });
            }
          }
        } catch (error) {
          console.error('Blog translation error:', error);
        }
      }
      
      setBlogSavingProgress(90);
      
      toast({
        title: t("admin.success"),
        description: editingBlog ? "Blog post updated successfully" : "Blog post created successfully",
      });

      setIsBlogDialogOpen(false);
      setEditingBlog(null);
      setBlogForm({
        title: "",
        slug: "",
        author: "",
        category: "",
        excerpt: "",
        content: "",
        featured_image_url: "",
        published: false,
      });
      setIsSlugManuallyEdited(false);
      
      setBlogSavingProgress(100);
      setTimeout(() => setBlogSavingProgress(0), 500);
      
      fetchBlogPosts();
    } catch (error: any) {
      setBlogSavingProgress(0);
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to save blog post",
        variant: "destructive",
      });
    }
  };

  const handleDeleteBlog = async (blogId: string) => {
    if (!confirm(t("admin.blog.deleteconfirm"))) return;

    try {
      const { error, count } = await supabase
        .from("blog_posts")
        .delete({ count: 'exact' })
        .eq("id", blogId);

      if (error) throw error;
      
      if (count === 0) {
        toast({
          title: t("admin.error"),
          description: "Cannot delete this blog post. You can only delete your own draft posts.",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: t("admin.success"),
        description: "Blog post deleted successfully",
      });
      fetchBlogPosts();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete blog post",
        variant: "destructive",
      });
    }
  };

  const handleEditBlog = (blog: BlogPost) => {
    setEditingBlog(blog);
    
    // Load the translation for the current language if it exists
    const translation = blog.translations?.[language];
    
    setBlogForm({
      title: translation?.title || blog.title,
      slug: blog.slug,
      author: translation?.author || blog.author,
      category: blog.category,
      excerpt: translation?.excerpt || blog.excerpt || "",
      content: translation?.content || blog.content,
      featured_image_url: blog.featured_image_url || "",
      published: blog.published,
    });
    setIsSlugManuallyEdited(true);
    setIsBlogDialogOpen(true);
  };

  const handleManageBlogTranslations = async (blog: BlogPost) => {
    setTranslatingBlog(blog);
    setIsBlogTranslationsDialogOpen(true);
    
    try {
      const { data, error } = await supabase
        .from("blog_posts_translations")
        .select("*")
        .eq("blog_post_id", blog.id);
      
      if (error) throw error;
      
      const translationsMap: Record<string, BlogTranslation> = {};
      if (data) {
        data.forEach(t => {
          translationsMap[t.language] = t;
        });
      }
      
      ['en', 'ru', 'es'].forEach(lang => {
        if (!translationsMap[lang]) {
          translationsMap[lang] = {
            blog_post_id: blog.id,
            language: lang,
            title: blog.title,
            excerpt: blog.excerpt || "",
            content: blog.content,
            author: blog.author,
          };
        }
      });
      
      setBlogTranslations(translationsMap);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSaveBlogTranslations = async () => {
    if (!translatingBlog) return;
    
    try {
      for (const lang of ['en', 'ru', 'es']) {
        const translation = blogTranslations[lang];
        if (translation && translation.title && translation.content) {
          const { error } = await supabase
            .from("blog_posts_translations")
            .upsert({
              blog_post_id: translatingBlog.id,
              language: lang,
              title: translation.title,
              excerpt: translation.excerpt || null,
              content: translation.content,
              author: translation.author,
            }, {
              onConflict: 'blog_post_id,language'
            });
          
          if (error) throw error;
        }
      }
      
      toast({
        title: t("admin.success"),
        description: "Blog translations saved successfully",
      });
      
      setIsBlogTranslationsDialogOpen(false);
      setTranslatingBlog(null);
      setBlogTranslations({});
      fetchBlogPosts();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to save translations",
        variant: "destructive",
      });
    }
  };

  const updateBlogTranslation = (lang: string, field: string, value: string) => {
    setBlogTranslations(prev => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value,
      }
    }));
  };

  // Course functions
  const fetchCourses = async () => {
    setIsLoadingCourses(true);
    try {
      // RLS policies now handle filtering by user for contributors
      const { data: courseData, error: courseError } = await supabase
        .from("courses")
        .select("*")
        .order("created_at", { ascending: false });

      if (courseError) throw courseError;

      const { data: translationsData, error: translationsError } = await supabase
        .from("courses_translations")
        .select("*");

      if (translationsError) throw translationsError;

      const coursesWithTranslations = (courseData || []).map(course => {
        const courseTranslations: Record<string, CourseTranslation> = {};
        (translationsData || [])
          .filter(t => t.course_id === course.id)
          .forEach(t => {
            courseTranslations[t.language] = t;
          });
        return { ...course, translations: courseTranslations };
      });

      setCourses(coursesWithTranslations);
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to fetch courses",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const handleCourseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCourseSavingProgress(10);
    
    try {
      let courseId = editingCourse?.id;
      
      if (editingCourse) {
        setCourseSavingProgress(20);
        
        const { error } = await supabase
          .from("courses")
          .update({
            title: courseForm.title,
            description: courseForm.description,
            instructor: courseForm.instructor,
            duration: courseForm.duration,
            level: courseForm.level,
            schedule: courseForm.schedule || null,
            max_students: courseForm.max_students ? parseInt(courseForm.max_students) : null,
            image_url: courseForm.image_url || null,
            published: courseForm.published,
          })
          .eq("id", editingCourse.id);

        if (error) throw error;
        setCourseSavingProgress(30);
      } else {
        setCourseSavingProgress(20);
        
        const { data, error } = await supabase
          .from("courses")
          .insert({
            title: courseForm.title,
            description: courseForm.description,
            instructor: courseForm.instructor,
            duration: courseForm.duration,
            level: courseForm.level,
            schedule: courseForm.schedule || null,
            max_students: courseForm.max_students ? parseInt(courseForm.max_students) : null,
            image_url: courseForm.image_url || null,
            published: courseForm.published,
            created_by: user?.id,
          })
          .select();

        if (error) throw error;
        courseId = data[0].id;
        setCourseSavingProgress(30);
      }
      
      // Save the current language version first (no translation needed)
      setCourseSavingProgress(40);
      await supabase
        .from("courses_translations")
        .upsert({
          course_id: courseId,
          language: language,
          title: courseForm.title,
          description: courseForm.description,
          instructor: courseForm.instructor,
          duration: courseForm.duration,
          level: courseForm.level,
          schedule: courseForm.schedule || null,
        }, {
          onConflict: 'course_id,language'
        });
      
      // Auto-translate to OTHER languages only (exclude current language)
      const targetLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
      
      if (targetLanguages.length > 0) {
        try {
          setCourseSavingProgress(50);
          
          // Translate title
          const { data: titleTranslations, error: titleError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: courseForm.title,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setCourseSavingProgress(55);
          
          // Translate description
          const { data: descTranslations, error: descError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: courseForm.description,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setCourseSavingProgress(60);
          
          // Translate instructor
          const { data: instructorTranslations, error: instructorError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: courseForm.instructor,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setCourseSavingProgress(65);
          
          // Translate duration
          const { data: durationTranslations, error: durationError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: courseForm.duration,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setCourseSavingProgress(70);
          
          // Translate level
          const { data: levelTranslations, error: levelError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: courseForm.level,
              fromLanguage: language,
              toLanguages: targetLanguages
            }
          });
          
          setCourseSavingProgress(75);
          
          // Translate schedule if present
          let scheduleTranslations = null;
          let scheduleError = null;
          if (courseForm.schedule) {
            const result = await supabase.functions.invoke('translate-content', {
              body: {
                text: courseForm.schedule,
                fromLanguage: language,
                toLanguages: targetLanguages
              }
            });
            scheduleTranslations = result.data;
            scheduleError = result.error;
          }
          
          setCourseSavingProgress(80);
          
          if (!titleError && !descError && !instructorError && !durationError && !levelError &&
              titleTranslations?.translations && descTranslations?.translations && 
              instructorTranslations?.translations && durationTranslations?.translations && 
              levelTranslations?.translations) {
            
            // Save translations for each target language
            for (const lang of targetLanguages) {
              await supabase
                .from("courses_translations")
                .upsert({
                  course_id: courseId,
                  language: lang,
                  title: titleTranslations.translations[lang] || courseForm.title,
                  description: descTranslations.translations[lang] || courseForm.description,
                  instructor: instructorTranslations.translations[lang] || courseForm.instructor,
                  duration: durationTranslations.translations[lang] || courseForm.duration,
                  level: levelTranslations.translations[lang] || courseForm.level,
                  schedule: scheduleTranslations?.translations?.[lang] || courseForm.schedule,
                }, {
                  onConflict: 'course_id,language'
                });
            }
          }
        } catch (error) {
          console.error('Course translation error:', error);
        }
      }
      
      setCourseSavingProgress(90);
      
      toast({
        title: t("admin.success"),
        description: editingCourse ? "Course updated successfully" : "Course created successfully",
      });

      setIsCourseDialogOpen(false);
      setEditingCourse(null);
      setCourseForm({
        title: "",
        description: "",
        instructor: "",
        duration: "",
        level: "",
        schedule: "",
        max_students: "",
        image_url: "",
        published: isAdmin,
      });
      
      setCourseSavingProgress(100);
      setTimeout(() => setCourseSavingProgress(0), 500);
      
      fetchCourses();
    } catch (error: any) {
      setCourseSavingProgress(0);
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to save course",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (!confirm(t("admin.bibleschool.deleteconfirm"))) return;

    try {
      const { error, count } = await supabase
        .from("courses")
        .delete({ count: 'exact' })
        .eq("id", courseId);

      if (error) throw error;
      
      if (count === 0) {
        toast({
          title: t("admin.error"),
          description: "Cannot delete this course. You can only delete your own draft courses.",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: t("admin.success"),
        description: "Course deleted successfully",
      });
      fetchCourses();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete course",
        variant: "destructive",
      });
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    
    // Load the translation for the current language if it exists
    const translation = course.translations?.[language];
    
    setCourseForm({
      title: translation?.title || course.title,
      description: translation?.description || course.description,
      instructor: translation?.instructor || course.instructor,
      duration: translation?.duration || course.duration,
      level: translation?.level || course.level,
      schedule: translation?.schedule || course.schedule || "",
      max_students: course.max_students?.toString() || "",
      image_url: course.image_url || "",
      published: course.published,
    });
    setIsCourseDialogOpen(true);
  };

  const handleManageCourseTranslations = async (course: Course) => {
    setTranslatingCourse(course);
    setIsCourseTranslationsDialogOpen(true);
    
    try {
      const { data, error } = await supabase
        .from("courses_translations")
        .select("*")
        .eq("course_id", course.id);
      
      if (error) throw error;
      
      console.log('Fetched course translations:', data);
      
      const translationsMap: Record<string, CourseTranslation> = {};
      if (data) {
        data.forEach(t => {
          translationsMap[t.language] = {
            course_id: t.course_id,
            language: t.language,
            title: t.title,
            description: t.description,
            instructor: t.instructor,
            duration: t.duration,
            level: t.level,
            schedule: t.schedule,
          };
        });
      }
      
      // Initialize missing languages with course defaults
      ['en', 'ru', 'es'].forEach(lang => {
        if (!translationsMap[lang]) {
          translationsMap[lang] = {
            course_id: course.id,
            language: lang,
            title: course.title,
            description: course.description,
            instructor: course.instructor,
            duration: course.duration,
            level: course.level,
            schedule: course.schedule,
          };
        }
      });
      
      console.log('Initialized translations map:', translationsMap);
      setCourseTranslations(translationsMap);
    } catch (error: any) {
      console.error('Error fetching course translations:', error);
      toast({
        title: t("admin.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSaveCourseTranslations = async () => {
    if (!translatingCourse) return;
    
    try {
      console.log('Saving course translations:', courseTranslations);
      
      for (const lang of ['en', 'ru', 'es']) {
        const translation = courseTranslations[lang];
        console.log(`Processing ${lang} translation:`, translation);
        
        if (translation && translation.title && translation.description && translation.instructor && translation.duration && translation.level) {
          const dataToSave = {
            course_id: translatingCourse.id,
            language: lang,
            title: translation.title,
            description: translation.description,
            instructor: translation.instructor,
            duration: translation.duration,
            level: translation.level,
            schedule: translation.schedule,
          };
          
          console.log(`Upserting ${lang}:`, dataToSave);
          
          const { data, error } = await supabase
            .from("courses_translations")
            .upsert(dataToSave, {
              onConflict: 'course_id,language'
            })
            .select();
          
          if (error) {
            console.error(`Error saving ${lang} translation:`, error);
            throw error;
          }
          
          console.log(`Successfully saved ${lang}:`, data);
        } else {
          console.warn(`Skipping ${lang} - missing required fields:`, translation);
        }
      }
      
      toast({
        title: t("admin.success"),
        description: "Course translations saved successfully",
      });
      
      setIsCourseTranslationsDialogOpen(false);
      setTranslatingCourse(null);
      setCourseTranslations({});
      fetchCourses();
    } catch (error: any) {
      console.error('Error saving course translations:', error);
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to save translations",
        variant: "destructive",
      });
    }
  };

  const updateCourseTranslation = (lang: string, field: string, value: string) => {
    setCourseTranslations(prev => {
      const updated = {
        ...prev,
        [lang]: {
          ...prev[lang],
          course_id: translatingCourse?.id || prev[lang]?.course_id || '',
          language: lang,
          [field]: value,
        }
      };
      console.log('Updated course translations:', updated);
      return updated;
    });
  };

  // Site Content Functions
  const fetchSiteContent = async () => {
    setIsLoadingContent(true);
    try {
      // Fetch unique sections if not already fetched
      if (availableSections.length === 0) {
        const { data: sectionsData } = await supabase
          .from('site_content')
          .select('section')
          .order('section', { ascending: true });
        
        if (sectionsData) {
          const uniqueSections = [...new Set(sectionsData.map(item => item.section))];
          setAvailableSections(uniqueSections);
        }
      }

      // Fetch unique content keys
      const { data: keysData } = await supabase
        .from('site_content')
        .select('content_key')
        .order('content_key', { ascending: true });
      
      if (keysData) {
        const uniqueKeys = [...new Set(keysData.map(item => item.content_key))];
        setAvailableContentKeys(uniqueKeys);
        
        // Parse keys to create groups
        const groups: Record<string, string[]> = {};
        uniqueKeys.forEach(key => {
          const parts = key.split('.');
          if (parts.length >= 2) {
            const [group, ...rest] = parts;
            const suffix = rest.join('.');
            if (!groups[group]) {
              groups[group] = [];
            }
            if (!groups[group].includes(suffix)) {
              groups[group].push(suffix);
            }
          } else {
            if (!groups['other']) {
              groups['other'] = [];
            }
            groups['other'].push(key);
          }
        });
        setKeyGroups(groups);
      }

      // Fetch base English content (the master structure)
      let baseQuery = supabase
        .from('site_content')
        .select('*')
        .eq('language', 'en')
        .order('section', { ascending: true })
        .order('content_key', { ascending: true });
      
      if (selectedContentSection !== "all") {
        baseQuery = baseQuery.eq('section', selectedContentSection);
      }

      const { data: baseData, error: baseError } = await baseQuery;
      if (baseError) throw baseError;

      // If English is selected, just use the English data
      if (language === 'en') {
        setSiteContent(baseData || []);
        
        const grouped: Record<string, any[]> = {};
        (baseData || []).forEach(item => {
          if (!grouped[item.section]) {
            grouped[item.section] = [];
          }
          grouped[item.section].push(item);
        });
        setGroupedSections(grouped);
      } else {
        // For RU or ES, fetch translations and merge with English fallback
        let translationQuery = supabase
          .from('site_content')
          .select('*')
          .eq('language', language)
          .order('section', { ascending: true })
          .order('content_key', { ascending: true });
        
        if (selectedContentSection !== "all") {
          translationQuery = translationQuery.eq('section', selectedContentSection);
        }

        const { data: translationData, error: translationError } = await translationQuery;
        if (translationError) throw translationError;

        // Create a map of translations
        const translationMap = new Map();
        (translationData || []).forEach(t => {
          translationMap.set(`${t.section}:${t.content_key}`, t);
        });

        // Merge: use translation if exists, otherwise use English
        const mergedData = (baseData || []).map(baseItem => {
          const key = `${baseItem.section}:${baseItem.content_key}`;
          const translation = translationMap.get(key);
          
          if (translation) {
            return translation;
          } else {
            // Return English content but mark it as fallback
            return {
              ...baseItem,
              language: language,
              content_value: baseItem.content_value + ' [EN]',
              id: null // No ID means translation doesn't exist yet
            };
          }
        });

        setSiteContent(mergedData);
        
        const grouped: Record<string, any[]> = {};
        mergedData.forEach(item => {
          if (!grouped[item.section]) {
            grouped[item.section] = [];
          }
          grouped[item.section].push(item);
        });
        setGroupedSections(grouped);
      }
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to fetch content",
        variant: "destructive",
      });
    } finally {
      setIsLoadingContent(false);
    }
  };

  const handleSaveContent = async () => {
    const finalSection = isAddingNewSection ? customSection : contentForm.section;
    
    // Handle bulleted content structure
    if (isBulletedContent) {
      if (!finalSection || !bulletedPrefix || !bulletedHeading || bulletedTexts.some(t => !t.trim())) {
        toast({
          title: t("admin.error"),
          description: "Please fill in all required fields for bulleted content",
          variant: "destructive",
        });
        return;
      }

      try {
        if (isAddingNewKey) {
          // Creating new bulleted content - delete any existing entries with this prefix to avoid duplicates
          const { error: deleteError } = await supabase
            .from('site_content')
            .delete()
            .eq('section', finalSection)
            .like('content_key', `${bulletedPrefix}.%`);

          if (deleteError) console.error('Error cleaning existing entries:', deleteError);
        } else {
          // Editing existing bulleted content - delete old text entries that exceed the new count
          const { error: deleteExtraError } = await supabase
            .from('site_content')
            .delete()
            .eq('section', finalSection)
            .eq('language', contentForm.language)
            .like('content_key', `${bulletedPrefix}.text%`)
            .gt('content_key', `${bulletedPrefix}.text${bulletedTexts.length}`);

          if (deleteExtraError) console.error('Error cleaning extra text entries:', deleteExtraError);
        }

        // Upsert heading entry
        const headingData = {
          section: finalSection,
          content_key: `${bulletedPrefix}.heading`,
          content_value: bulletedHeading,
          language: contentForm.language
        };

        const { error: headingError } = await supabase
          .from('site_content')
          .upsert(headingData, {
            onConflict: 'section,content_key,language'
          });

        if (headingError) throw headingError;

        // Upsert text entries
        for (let i = 0; i < bulletedTexts.length; i++) {
          const textData = {
            section: finalSection,
            content_key: `${bulletedPrefix}.text${i + 1}`,
            content_value: bulletedTexts[i],
            language: contentForm.language
          };

          const { error: textError } = await supabase
            .from('site_content')
            .upsert(textData, {
              onConflict: 'section,content_key,language'
            });

          if (textError) throw textError;
        }

        // Auto-translate to other languages (always sync translations)
        try {
          const targetLanguages = ['en', 'ru', 'es'].filter(lang => lang !== contentForm.language);
          
          // Translate and sync heading
          const { data: translationData, error: translationError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: bulletedHeading,
              fromLanguage: contentForm.language,
              toLanguages: targetLanguages
            }
          });

          if (!translationError && translationData?.translations) {
            for (const [lang, translatedText] of Object.entries(translationData.translations)) {
              await supabase.from('site_content').upsert({
                section: finalSection,
                content_key: `${bulletedPrefix}.heading`,
                content_value: translatedText as string,
                language: lang
              }, {
                onConflict: 'section,content_key,language'
              });
            }
          }

          // Translate and sync all text items
          for (let i = 0; i < bulletedTexts.length; i++) {
            const { data: textTransData, error: textTransError } = await supabase.functions.invoke('translate-content', {
              body: {
                text: bulletedTexts[i],
                fromLanguage: contentForm.language,
                toLanguages: targetLanguages
              }
            });

            if (!textTransError && textTransData?.translations) {
              for (const [lang, translatedText] of Object.entries(textTransData.translations)) {
                await supabase.from('site_content').upsert({
                  section: finalSection,
                  content_key: `${bulletedPrefix}.text${i + 1}`,
                  content_value: translatedText as string,
                  language: lang
                }, {
                  onConflict: 'section,content_key,language'
                });
              }
            }
          }
          
          // Clean up excess text entries in other languages if count was reduced
          if (!isAddingNewKey) {
            for (const lang of targetLanguages) {
              const { error: cleanupError } = await supabase
                .from('site_content')
                .delete()
                .eq('section', finalSection)
                .eq('language', lang)
                .like('content_key', `${bulletedPrefix}.text%`)
                .gt('content_key', `${bulletedPrefix}.text${bulletedTexts.length}`);
              
              if (cleanupError) console.error(`Error cleaning up ${lang} text entries:`, cleanupError);
            }
          }
        } catch (translationError) {
          console.error('Translation error:', translationError);
        }

        toast({
          title: t("admin.success"),
          description: isAddingNewKey ? "Bulleted content created successfully" : "Bulleted content updated successfully",
        });

        setIsContentDialogOpen(false);
        setIsBulletedContent(false);
        setBulletedPrefix("");
        setBulletedHeading("");
        setBulletedTexts([""]);
        setCustomKey("");
        setIsAddingNewKey(false);
        setSelectedKeySuffix("");
        
        // Refresh content list and open section edit dialog
        await fetchSiteContent();
        await openSectionFieldsDialog(finalSection);
        
        return;
      } catch (error: any) {
        toast({
          title: t("admin.error"),
          description: error.message,
          variant: "destructive",
        });
        return;
      }
    }
    
    let finalKey = "";
    
    if (isAddingNewKey) {
      finalKey = customKey;
    } else if (selectedKeySuffix) {
      finalKey = selectedKeySuffix;
    } else {
      finalKey = contentForm.content_key;
    }

    if (!finalSection || !finalKey || !contentForm.content_value) {
      toast({
        title: t("admin.error"),
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('site_content')
        .upsert({
          section: finalSection,
          content_key: finalKey,
          content_value: contentForm.content_value,
          language: contentForm.language,
          id: editingContent?.id,
        }, {
          onConflict: 'section,content_key,language'
        });

      if (error) throw error;

      // If saving English content, automatically translate to other languages
      if (contentForm.language === 'en') {
        try {
          // Call translate-content edge function
          const { data: translationData, error: translationError } = await supabase.functions.invoke('translate-content', {
            body: {
              text: contentForm.content_value,
              fromLanguage: 'en',
              toLanguages: ['ru', 'es']
            }
          });

          if (translationError) {
            console.error('Translation error:', translationError);
            // If translation fails, fall back to copying English
            const otherLanguages = ['ru', 'es'];
            for (const lang of otherLanguages) {
              const { data: existing } = await supabase
                .from('site_content')
                .select('id')
                .eq('section', finalSection)
                .eq('content_key', finalKey)
                .eq('language', lang)
                .maybeSingle();

              if (!existing) {
                await supabase
                  .from('site_content')
                  .insert({
                    section: finalSection,
                    content_key: finalKey,
                    content_value: contentForm.content_value,
                    language: lang,
                  });
              }
            }
          } else if (translationData?.translations) {
            // Use translated content
            const translations = translationData.translations;
            for (const [lang, translatedText] of Object.entries(translations)) {
              const { data: existing } = await supabase
                .from('site_content')
                .select('id')
                .eq('section', finalSection)
                .eq('content_key', finalKey)
                .eq('language', lang)
                .maybeSingle();

              if (!existing) {
                await supabase
                  .from('site_content')
                  .insert({
                    section: finalSection,
                    content_key: finalKey,
                    content_value: translatedText as string,
                    language: lang,
                  });
              }
            }
          }
        } catch (translationError) {
          console.error('Translation failed:', translationError);
          // Continue without translations - English content is already saved
        }
      }

      toast({
        title: t("admin.success"),
        description: editingContent ? "Content updated successfully" : "Content created successfully",
      });

      setIsContentDialogOpen(false);
      setEditingContent(null);
      setContentForm({ section: "", content_key: "", content_value: "", language: "en" });
      setIsAddingNewSection(false);
      setIsAddingNewKey(false);
      setCustomSection("");
      setCustomKey("");
      setSelectedKeyGroup("");
      setSelectedKeySuffix("");
      
      // Reset available sections to force refetch
      setAvailableSections([]);
      fetchSiteContent();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to save content",
        variant: "destructive",
      });
    }
  };

  const handleDeleteContent = async (id: string) => {
    if (!confirm("Are you sure you want to delete this content?")) return;

    try {
      const { error } = await supabase
        .from('site_content')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: "Content deleted successfully",
      });

      fetchSiteContent();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete content",
        variant: "destructive",
      });
    }
  };


  const openContentDialog = (content?: any) => {
    if (content) {
      setEditingContent(content);
      setContentForm({
        section: content.section,
        content_key: content.content_key,
        content_value: content.content_value,
        language: content.language,
      });
      
      // Parse existing key into group and suffix
      const parts = content.content_key.split('.');
      if (parts.length >= 2) {
        setSelectedKeyGroup(parts[0]);
        setSelectedKeySuffix(parts.slice(1).join('.'));
      } else {
        setSelectedKeyGroup('other');
        setSelectedKeySuffix(content.content_key);
      }
      
      setIsAddingNewSection(false);
      setIsAddingNewKey(false);
    } else {
      setEditingContent(null);
      setContentForm({ section: "", content_key: "", content_value: "", language: language });
      setIsAddingNewSection(false);
      setIsAddingNewKey(false);
      setCustomSection("");
      setCustomKey("");
      setSelectedKeyGroup("");
      setSelectedKeySuffix("");
      
      // Reset bulleted content state
      setIsBulletedContent(false);
      setBulletedPrefix("");
      setBulletedHeading("");
      setBulletedTexts([""]);
      
      // Force refresh available keys when opening to add new content
      setAvailableContentKeys([]);
      setKeyGroups({});
    }
    setIsContentDialogOpen(true);
  };

  const openSectionFieldsDialog = async (sectionName: string) => {
    // Always fetch fresh data from database to ensure we have the latest fields
    let freshFields: any[] = [];
    
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('*')
        .eq('section', sectionName)
        .eq('language', language)
        .order('content_key', { ascending: true });
      
      if (error) throw error;
      freshFields = data || [];
    } catch (error) {
      console.error('Error fetching section fields:', error);
      // Fallback to cached data if fetch fails
      freshFields = groupedSections[sectionName] || [];
    }
    
    setSelectedSectionFields(freshFields);
    setEditingSectionName(sectionName);
    
    // Fetch all possible values for each content_key in this section ONLY
    const valueOptions: Record<string, string[]> = {};
    const customValues: Record<string, boolean> = {};
    
    for (const field of freshFields) {
      const { data } = await supabase
        .from('site_content')
        .select('content_value')
        .eq('section', sectionName)
        .eq('content_key', field.content_key)
        .eq('language', language)
        .order('content_value', { ascending: true });
      
      if (data) {
        const uniqueValues = [...new Set(data.map(item => item.content_value))];
        valueOptions[field.content_key] = uniqueValues;
      }
      
      // Initialize custom field tracking
      customValues[field.id] = false;
    }
    
    setFieldValueOptions(valueOptions);
    setCustomFieldValues(customValues);
    setIsSectionFieldsDialogOpen(true);
  };

  const handleUpdateSectionField = (fieldId: string, newValue: string) => {
    setSelectedSectionFields(prev => 
      prev.map(field => 
        field.id === fieldId ? { ...field, content_value: newValue } : field
      )
    );
  };

  const toggleCustomFieldValue = (fieldId: string) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldId]: !prev[fieldId]
    }));
  };

  const handleSaveSectionFields = async () => {
    try {
      setIsSaving(true);
      setSavingProgress(0);
      setSavingMessage(language === 'en' ? 'Preparing updates...' : 
                       language === 'ru' ? 'Подготовка обновлений...' : 
                       'Preparando actualizaciones...');
      
      const updatePromises = [];
      const newFieldsToCreate: Array<{ section: string; content_key: string; content_value: string; language: string }> = [];
      const totalFields = selectedSectionFields.length;
      let processedFields = 0;

      // Group bulleted content to ensure all fields are synced
      const bulletedGroups: Record<string, typeof selectedSectionFields> = {};
      selectedSectionFields.forEach(field => {
        const bulletedMatch = field.content_key.match(/^(bulleted\d+)\.(heading|text\d+)$/);
        if (bulletedMatch) {
          const prefix = bulletedMatch[1];
          if (!bulletedGroups[prefix]) {
            bulletedGroups[prefix] = [];
          }
          bulletedGroups[prefix].push(field);
        }
      });

      // Process each field
      for (const field of selectedSectionFields) {
        const cleanValue = field.content_value.replace(/ \[EN\]$/, '');
        
        setSavingMessage(language === 'en' ? `Processing field ${processedFields + 1} of ${totalFields}...` :
                         language === 'ru' ? `Обработка поля ${processedFields + 1} из ${totalFields}...` :
                         `Procesando campo ${processedFields + 1} de ${totalFields}...`);
        
        if (!field.id) {
          // Field doesn't exist - need to create for all languages
          newFieldsToCreate.push({
            section: field.section,
            content_key: field.content_key,
            content_value: cleanValue,
            language: language, // Create for current language
          });
        } else {
          // Update existing field
          const updatePromise = supabase
            .from('site_content')
            .update({ content_value: cleanValue })
            .eq('id', field.id);
          
          updatePromises.push(updatePromise);
          
          // Translate and update/create the same field in other languages
          const otherLanguages = ['en', 'ru', 'es'].filter(lang => lang !== language);
          
          if (otherLanguages.length > 0) {
            try {
              setSavingMessage(language === 'en' ? `Translating field ${processedFields + 1}...` :
                               language === 'ru' ? `Перевод поля ${processedFields + 1}...` :
                               `Traduciendo campo ${processedFields + 1}...`);
              
              // Call translation edge function
              const { data: translationData, error: translationError } = await supabase.functions.invoke('translate-content', {
                body: {
                  text: cleanValue,
                  fromLanguage: language,
                  toLanguages: otherLanguages
                }
              });

              if (translationError) {
                console.error('Translation error:', translationError);
              } else if (translationData?.translations) {
                // Update/create each language with its translation
                for (const [targetLang, translatedText] of Object.entries(translationData.translations)) {
                  await supabase
                    .from('site_content')
                    .upsert({
                      section: field.section,
                      content_key: field.content_key,
                      content_value: translatedText as string,
                      language: targetLang
                    }, {
                      onConflict: 'section,content_key,language'
                    });
                }
              }
            } catch (error) {
              console.error('Translation failed, skipping translation:', error);
            }
          }
        }
        
        processedFields++;
        setSavingProgress((processedFields / totalFields) * 70); // 70% for processing
      }

      // Execute all updates in parallel
      setSavingMessage(language === 'en' ? 'Saving to database...' :
                       language === 'ru' ? 'Сохранение в базу данных...' :
                       'Guardando en la base de datos...');
      setSavingProgress(80);
      
      if (updatePromises.length > 0) {
        const results = await Promise.all(updatePromises);
        
        // Check for errors in any update
        const errors = results.filter(result => result.error);
        if (errors.length > 0) {
          console.error('Update errors:', errors);
        }
      }

      // Insert new fields for current language
      if (newFieldsToCreate.length > 0) {
        const { error: insertError } = await supabase
          .from('site_content')
          .insert(newFieldsToCreate);
        
        if (insertError) throw insertError;
      }

      setSavingProgress(95);
      setSavingMessage(language === 'en' ? 'Refreshing content...' :
                       language === 'ru' ? 'Обновление содержимого...' :
                       'Actualizando contenido...');
      
      await fetchSiteContent();
      
      setSavingProgress(100);
      setSavingMessage(language === 'en' ? 'Complete!' :
                       language === 'ru' ? 'Завершено!' :
                       '¡Completado!');

      toast({
        title: t("admin.success"),
        description: language === 'en' ? 'Content updated and translated across all languages' :
                     language === 'ru' ? 'Содержимое обновлено и переведено на все языки' :
                     'Contenido actualizado y traducido a todos los idiomas',
      });

      setTimeout(() => {
        setIsSectionFieldsDialogOpen(false);
        setIsSaving(false);
        setSavingProgress(0);
        setSavingMessage("");
      }, 500);
    } catch (error: any) {
      setIsSaving(false);
      setSavingProgress(0);
      setSavingMessage("");
      
      toast({
        title: t("admin.error"),
        description: error.message || (language === 'en' ? 'Failed to update section content' :
                     language === 'ru' ? 'Не удалось обновить содержимое раздела' :
                     'Error al actualizar el contenido de la sección'),
        variant: "destructive",
      });
    }
  };

  const handleDeleteSectionField = async (fieldId: string) => {
    if (!confirm("Are you sure you want to delete this field from ALL languages?")) return;

    try {
      // Find the field to get its section and content_key
      const field = selectedSectionFields.find(f => f.id === fieldId);
      if (!field) return;

      // Delete all entries with this section and content_key (all languages)
      const { error } = await supabase
        .from('site_content')
        .delete()
        .eq('section', field.section)
        .eq('content_key', field.content_key);

      if (error) throw error;

      toast({
        title: t("admin.success"),
        description: "Field deleted from all languages",
      });

      // Clear cached keys to force refresh
      setAvailableContentKeys([]);
      setKeyGroups({});

      // Remove from local state - remove all matching content_keys
      setSelectedSectionFields(prev => 
        prev.filter(f => f.content_key !== field.content_key)
      );
      
      // If no fields left, close the dialog and refresh
      if (selectedSectionFields.filter(f => f.content_key !== field.content_key).length === 0) {
        setIsSectionFieldsDialogOpen(false);
      }
      
      fetchSiteContent();
    } catch (error: any) {
      toast({
        title: t("admin.error"),
        description: error.message || "Failed to delete field",
        variant: "destructive",
      });
    }
  };

  const categoryTotals = getTotalByCategory();
  const grandTotal = donations.reduce((sum, d) => sum + Number(d.amount), 0);
  const totalProcessingFees = donations.reduce((sum, d) => sum + Number(d.processing_fee || 0), 0);
  const totalDonationsOnly = grandTotal - totalProcessingFees;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-4xl font-bold text-foreground">{t("admin.title")}</h1>
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <Button
                    onClick={() => navigate('/security-monitoring')}
                    variant="outline"
                    className="gap-2"
                  >
                    <Shield className="h-4 w-4" />
                    Security Monitoring
                  </Button>
                )}
                {isViewer && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400">
                    {t("admin.viewermode")}
                </span>
              )}
            </div>
            </div>
            <p className="text-lg text-muted-foreground">
              Logged in as <span className="font-semibold text-foreground">{user?.email}</span>
              {" · "}
              <span className="text-primary">
                {isAdmin ? "Admin" : isViewer ? "Viewer" : isBlogEditor ? "Blog Editor" : isBibleSchoolEditor ? "Bible School Editor" : isAnnouncementEditor ? "Announcement Editor" : isSermonEditor ? "Sermon Editor" : "Staff"}
              </span>
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex flex-col md:grid md:grid-cols-10 w-full mb-8 h-auto gap-2">
              {(isAdmin || isViewer) && (
                <TabsTrigger value="donations" className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">{t("admin.tabs.donations")}</TabsTrigger>
              )}
              {(isAdmin || isViewer || isAnnouncementEditor) && (
                <TabsTrigger value="announcements" className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2 min-w-[100px]">
                  <BookOpen className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span>{t("admin.tabs.announcements")}</span>
                </TabsTrigger>
              )}
              {(isAdmin || isViewer || isSermonEditor) && (
                <TabsTrigger value="sermons" onClick={fetchSermons} className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <BookOpen className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.sermons")}</span>
                </TabsTrigger>
              )}
              {(isAdmin || isBlogEditor) && (
                <TabsTrigger value="blog" onClick={fetchBlogPosts} className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <BookOpen className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.blog")}</span>
                </TabsTrigger>
              )}
              {(isAdmin || isBibleSchoolEditor) && (
                <TabsTrigger value="bibleschool" onClick={fetchCourses} className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <BookOpen className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.bibleschool")}</span>
                </TabsTrigger>
              )}
              {(isAdmin || isBibleSchoolEditor) && (
                <TabsTrigger value="bibleschool-gallery" className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <ImageIcon className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.bibleschool.gallery")}</span>
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="youtube" className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <BookOpen className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">YouTube Videos</span>
                </TabsTrigger>
              )}
              {(isAdmin || isMusicEditor) && (
                <TabsTrigger value="music" className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <Music className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.music.title")}</span>
                </TabsTrigger>
              )}
              {(isAdmin || isViewer) && (
                <TabsTrigger value="photos" onClick={fetchMissionTrips} className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <ImageIcon className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.photos")}</span>
                </TabsTrigger>
              )}
              {(isAdmin || isViewer) && (
                <TabsTrigger value="specialevents" onClick={fetchSpecialEvents} className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <ImageIcon className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.specialevents")}</span>
                </TabsTrigger>
              )}
              {(isAdmin) && (
                <TabsTrigger value="contributors" onClick={fetchContributorRequests} className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <UserPlus className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.contributors")}</span>
                </TabsTrigger>
              )}
              {(isAdmin) && (
                <TabsTrigger value="users" onClick={fetchUsers} className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <Users className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.users")}</span>
                </TabsTrigger>
              )}
              {(isAdmin) && (
                <TabsTrigger value="members" className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <UserCog className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.members")}</span>
                </TabsTrigger>
              )}
              {(isAdmin) && (
                <TabsTrigger value="content" onClick={fetchSiteContent} className="text-xs sm:text-sm whitespace-normal h-auto py-2 px-2">
                  <Edit className="w-4 h-4 mr-1 md:mr-2 hidden md:inline" />
                  <span className="line-clamp-2">{t("admin.tabs.content")}</span>
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="donations">

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">{t("admin.donations.totalreceived")}</h3>
              <p className="text-3xl font-bold text-primary">${grandTotal.toFixed(2)}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("admin.donations.includingfees")}</p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">{t("admin.donations.netdonations")}</h3>
              <p className="text-3xl font-bold text-primary">${totalDonationsOnly.toFixed(2)}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("admin.donations.excludingfees")}</p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">{t("admin.donations.processingfees")}</h3>
              <p className="text-3xl font-bold text-orange-600">${totalProcessingFees.toFixed(2)}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("admin.donations.stripefees")}</p>
            </Card>
            
            {Object.entries(categoryTotals).map(([category, total]) => (
              <Card key={category} className="p-6">
                <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                  {getCategoryName(category)}
                </h3>
                <p className="text-3xl font-bold text-primary">${total.toFixed(2)}</p>
              </Card>
            ))}
          </div>

          {/* Filter */}
          <Card className="p-6 mb-6">
            <div className="flex items-center gap-4">
              <label className="font-semibold">{t("admin.donations.filtercategory")}</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("admin.donations.allcategories")}</SelectItem>
                  <SelectItem value="guatemala">{t("admin.category.guatemala")}</SelectItem>
                  <SelectItem value="mexico">{t("admin.category.mexico")}</SelectItem>
                  <SelectItem value="cuba">{t("admin.category.cuba")}</SelectItem>
                  <SelectItem value="church">{t("admin.category.church")}</SelectItem>
                  <SelectItem value="tithe">{t("admin.category.tithe")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Card>

          {/* Donations Table */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold mb-4">
              {t("admin.donations.recent")} ({filteredDonations.length})
            </h2>
            
            {isLoading ? (
              <p className="text-center py-8">{t("admin.donations.loading")}</p>
            ) : filteredDonations.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">{t("admin.donations.nodonations")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-semibold">{t("admin.donations.date")}</th>
                      <th className="text-left p-3 font-semibold">{t("admin.donations.donor")}</th>
                      <th className="text-left p-3 font-semibold">{t("admin.donations.email")}</th>
                      <th className="text-left p-3 font-semibold">{t("admin.donations.category")}</th>
                      <th className="text-right p-3 font-semibold">{t("admin.donations.donation")}</th>
                      <th className="text-right p-3 font-semibold">{t("admin.donations.processingfee")}</th>
                      <th className="text-right p-3 font-semibold">{t("admin.donations.total")}</th>
                      <th className="text-center p-3 font-semibold">{t("admin.donations.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDonations.map((donation) => {
                      const netAmount = Number(donation.amount) - Number(donation.processing_fee || 0);
                      return (
                        <tr key={donation.id} className="border-b hover:bg-muted/50">
                          <td className="p-3">
                            {new Date(donation.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3">{donation.donor_name}</td>
                          <td className="p-3">{donation.donor_email}</td>
                          <td className="p-3">{getCategoryName(donation.category)}</td>
                          <td className="p-3 text-right">
                            ${Number(donation.amount).toFixed(2)}
                          </td>
                          <td className="p-3 text-right text-muted-foreground">
                            ${Number(donation.processing_fee || 0).toFixed(2)}
                          </td>
                          <td className="p-3 text-right font-semibold">
                            ${netAmount.toFixed(2)}
                          </td>
                          <td className="p-3 text-center">
                            {isAdmin && (
                              <Button
                                variant="destructive"
                                size="icon"
                                onClick={() => handleDelete(donation.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
            </TabsContent>

            <TabsContent value="contributors">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">{t("admin.contributors.title")}</h2>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("admin.contributors.status.all")}</SelectItem>
                      <SelectItem value="pending">{t("admin.contributors.status.pending")}</SelectItem>
                      <SelectItem value="approved">{t("admin.contributors.status.approved")}</SelectItem>
                      <SelectItem value="rejected">{t("admin.contributors.status.rejected")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {isLoadingRequests ? (
                  <p className="text-center py-8">{t("admin.contributors.loading")}</p>
                ) : filteredRequests.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">{t("admin.contributors.norequests")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                       <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">{t("admin.contributors.field.name")}</th>
                          <th className="text-left p-3">{t("admin.contributors.field.email")}</th>
                          <th className="text-left p-3">{t("admin.contributors.field.role")}</th>
                          <th className="text-left p-3 min-w-[200px]">{t("admin.contributors.field.message")}</th>
                          <th className="text-left p-3">{t("admin.contributors.field.status")}</th>
                          <th className="text-left p-3">{t("admin.contributors.field.date")}</th>
                          <th className="text-center p-3">{t("admin.contributors.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRequests.map((request) => (
                          <tr key={request.id} className="border-b hover:bg-muted/50">
                            <td className="p-3">{request.full_name}</td>
                            <td className="p-3">{request.email}</td>
                            <td className="p-3">{getRoleName(request.requested_role)}</td>
                            <td className="p-3 min-w-[200px]">
                              <div className="max-w-xs" title={request.message || 'No message'}>
                                {request.message || '-'}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-1 rounded ${
                                request.status === 'pending' 
                                  ? "bg-yellow-100 text-yellow-800" 
                                  : request.status === 'approved'
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}>
                                {request.status}
                              </span>
                            </td>
                            <td className="p-3">{new Date(request.created_at).toLocaleDateString()}</td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-2">
                                {request.status === 'pending' && isAdmin && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleApproveRequest(request)}
                                      title={t("admin.contributors.approve")}
                                      className="text-green-600 hover:text-green-700"
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleRejectRequest(request.id, request.email, request.full_name, request.requested_role)}
                                      title={t("admin.contributors.reject")}
                                      className="text-orange-600 hover:text-orange-700"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                {isAdmin && (
                                  <Button
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleDeleteRequest(request.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="users">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">{t("admin.users.title")}</h2>
                  {isAdmin && (
                    <Dialog open={isCreateUserDialogOpen} onOpenChange={setIsCreateUserDialogOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="w-4 h-4 mr-2" />
                          {t("admin.users.createuser")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t("admin.users.createnew")}</DialogTitle>
                        <DialogDescription>
                          {t("admin.users.createdescription")}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateUser} className="space-y-4">
                        <div>
                          <Label htmlFor="new-email">{t("admin.users.email")} *</Label>
                          <Input
                            id="new-email"
                            type="email"
                            value={createUserForm.email}
                            onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="new-password">{t("admin.users.password")} *</Label>
                          <Input
                            id="new-password"
                            type="password"
                            value={createUserForm.password}
                            onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                            required
                            minLength={6}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("admin.users.passwordhint")}
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="new-role">{t("admin.users.assignrole")}</Label>
                          <Select
                            value={createUserForm.role}
                            onValueChange={(value: "none" | "admin" | "viewer" | "blog_editor" | "bible_school_editor" | "announcement_editor" | "sermon_editor" | "member" | "music_editor" | "music_viewer") => 
                              setCreateUserForm({ ...createUserForm, role: value })
                            }
                          >
                            <SelectTrigger id="new-role">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-background border-border z-[100] max-h-[300px] overflow-y-auto">
                              <SelectItem value="none">{t("admin.users.norole")}</SelectItem>
                              {isPrimaryAdmin && (
                                <SelectItem value="admin">{t("admin.users.admin")}</SelectItem>
                              )}
                              <SelectItem value="viewer">{t("admin.users.viewer")}</SelectItem>
                              <SelectItem value="blog_editor">Blog Editor</SelectItem>
                              <SelectItem value="bible_school_editor">Bible School Editor</SelectItem>
                              <SelectItem value="announcement_editor">Announcement Editor</SelectItem>
                              <SelectItem value="sermon_editor">Sermon Editor</SelectItem>
                              <SelectItem value="music_editor">Music Editor</SelectItem>
                              <SelectItem value="music_viewer">Music Viewer</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsCreateUserDialogOpen(false)}>
                            {t("admin.users.cancel")}
                          </Button>
                          <Button type="submit">
                            {t("admin.users.create")}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  )}
                </div>
                
                {isLoadingUsers ? (
                  <p className="text-center py-8">{t("admin.users.loading")}</p>
                ) : users.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">{t("admin.users.nousers")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-semibold">{t("admin.users.email")}</th>
                          <th className="text-left p-3 font-semibold">{t("admin.users.roles")}</th>
                          <th className="text-left p-3 font-semibold">{t("admin.users.created")}</th>
                          <th className="text-left p-3 font-semibold">{t("admin.users.lastsignin")}</th>
                          <th className="text-center p-3 font-semibold">{t("admin.users.actions")}</th>
                        </tr>
                      </thead>
                       <tbody>
                         {users.map((appUser, index) => {
                          const userIsAdmin = appUser.roles?.some(r => r.role === 'admin');
                          const userIsViewer = appUser.roles?.some(r => r.role === 'viewer');
                          const userIsBlogEditor = appUser.roles?.some(r => r.role === 'blog_editor');
                          const userIsBibleSchoolEditor = appUser.roles?.some(r => r.role === 'bible_school_editor');
                          const userIsAnnouncementEditor = appUser.roles?.some(r => r.role === 'announcement_editor');
                          const userIsSermonEditor = appUser.roles?.some(r => r.role === 'sermon_editor');
                          const userIsMusicEditor = appUser.roles?.some(r => r.role === 'music_editor');
                          const userIsMusicViewer = appUser.roles?.some(r => r.role === 'music_viewer');
                          const userIsMember = appUser.roles?.some(r => r.role === 'member');
                          const userIsPrimaryAdmin = appUser.roles?.some(r => r.role === 'admin' && r.is_primary);
                          const userIsProtectedAdmin = userIsPrimaryAdmin;
                          const hasAnyRole = userIsAdmin || userIsViewer || userIsBlogEditor || userIsBibleSchoolEditor || userIsAnnouncementEditor || userIsSermonEditor || userIsMusicEditor || userIsMusicViewer || userIsMember;
                          
                          return (
                            <tr key={appUser.id} className="border-b hover:bg-muted/50">
                              <td className="p-3">{appUser.email}</td>
                              <td className="p-3">
                                <div className="flex flex-col gap-2">
                                  <div className="flex gap-2 flex-wrap">
                                    {userIsAdmin && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                        {t("admin.users.admin")} {userIsPrimaryAdmin && `(${t("admin.users.primary")})`}
                                      </span>
                                    )}
                                    {userIsViewer && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400">
                                        {t("admin.users.viewer")}
                                      </span>
                                    )}
                                    {userIsBlogEditor && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-600 dark:text-purple-400">
                                        Blog Editor
                                      </span>
                                    )}
                                    {userIsBibleSchoolEditor && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400">
                                        Bible School Editor
                                      </span>
                                    )}
                                    {userIsAnnouncementEditor && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">
                                        Announcement Editor
                                      </span>
                                    )}
                                    {userIsSermonEditor && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                        Sermon Editor
                                      </span>
                                    )}
                                    {userIsMusicEditor && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-500/10 text-pink-600 dark:text-pink-400">
                                        Music Editor
                                      </span>
                                    )}
                                    {userIsMusicViewer && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
                                        Music Viewer
                                      </span>
                                    )}
                                    {userIsMember && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                                        Member
                                      </span>
                                    )}
                                    {!hasAnyRole && (
                                      <span className="text-muted-foreground text-xs">{t("admin.users.norole")}</span>
                                    )}
                                  </div>
                                   <div className="flex gap-1 flex-wrap">
                                    {isAdmin && (
                                      <>
                                        {!userIsAdmin && isPrimaryAdmin && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'admin')}
                                            className="text-xs h-7"
                                          >
                                            {t("admin.users.makeadmin")}
                                          </Button>
                                        )}
                                        {userIsAdmin && !userIsProtectedAdmin && isPrimaryAdmin && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'admin')}
                                            className="text-xs h-7"
                                          >
                                            {t("admin.users.removeadmin")}
                                          </Button>
                                        )}
                                        {!userIsViewer && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'viewer')}
                                            className="text-xs h-7"
                                          >
                                            {t("admin.users.makeviewer")}
                                          </Button>
                                        )}
                                        {userIsViewer && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'viewer')}
                                            className="text-xs h-7"
                                          >
                                            {t("admin.users.removeviewer")}
                                          </Button>
                                        )}
                                        {!userIsBlogEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'blog_editor')}
                                            className="text-xs h-7"
                                          >
                                            + Blog Editor
                                          </Button>
                                        )}
                                        {userIsBlogEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'blog_editor')}
                                            className="text-xs h-7"
                                          >
                                            - Blog Editor
                                          </Button>
                                        )}
                                        {!userIsBibleSchoolEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'bible_school_editor')}
                                            className="text-xs h-7"
                                          >
                                            + Bible School
                                          </Button>
                                        )}
                                        {userIsBibleSchoolEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'bible_school_editor')}
                                            className="text-xs h-7"
                                          >
                                            - Bible School
                                          </Button>
                                        )}
                                        {!userIsAnnouncementEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'announcement_editor')}
                                            className="text-xs h-7"
                                          >
                                            + Announcement
                                          </Button>
                                        )}
                                        {userIsAnnouncementEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'announcement_editor')}
                                            className="text-xs h-7"
                                          >
                                            - Announcement
                                          </Button>
                                        )}
                                        {!userIsSermonEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'sermon_editor')}
                                            className="text-xs h-7"
                                          >
                                            + Sermon
                                          </Button>
                                        )}
                                        {userIsSermonEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'sermon_editor')}
                                            className="text-xs h-7"
                                          >
                                            - Sermon
                                          </Button>
                                        )}
                                        {!userIsMusicViewer && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'music_viewer')}
                                            className="text-xs h-7"
                                          >
                                            + Music Viewer
                                          </Button>
                                        )}
                                        {userIsMusicViewer && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'music_viewer')}
                                            className="text-xs h-7"
                                          >
                                            - Music Viewer
                                          </Button>
                                        )}
                                        {!userIsMusicEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'music_editor')}
                                            className="text-xs h-7"
                                          >
                                            + Music Editor
                                          </Button>
                                        )}
                                        {userIsMusicEditor && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'music_editor')}
                                            className="text-xs h-7"
                                          >
                                            - Music Editor
                                          </Button>
                                        )}
                                        {!userIsMember && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleAssignRole(appUser.id, 'member')}
                                            className="text-xs h-7"
                                          >
                                            + Member
                                          </Button>
                                        )}
                                        {userIsMember && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRemoveRole(appUser.id, 'member')}
                                            className="text-xs h-7"
                                          >
                                            - Member
                                          </Button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="p-3">
                                {new Date(appUser.created_at).toLocaleDateString()}
                              </td>
                              <td className="p-3">
                                {appUser.last_sign_in_at 
                                  ? new Date(appUser.last_sign_in_at).toLocaleDateString()
                                  : t("admin.users.never")}
                              </td>
                              <td className="p-3 text-center">
                                {isAdmin && appUser.id !== user?.id && (
                                  <Button
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleDeleteUser(appUser.id, appUser.email)}
                                    disabled={userIsProtectedAdmin}
                                    className={userIsProtectedAdmin ? "opacity-50 cursor-not-allowed" : ""}
                                    title={userIsProtectedAdmin ? t("admin.users.cannotdelete") : t("admin.users.deleteuser")}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                                {appUser.id === user?.id && (
                                  <span className="text-xs text-muted-foreground">{t("admin.users.yourself")}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
              
              {/* Member Registration Dialog */}
              <Dialog open={isMemberFormDialogOpen} onOpenChange={setIsMemberFormDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Complete Member Registration</DialogTitle>
                    <DialogDescription>
                      Enter the member details to complete their registration. The member role will be automatically assigned.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmitMemberForm} className="space-y-4">
                    <div>
                      <Label htmlFor="member-full-name">Full Name *</Label>
                      <Input
                        id="member-full-name"
                        value={memberFormData.full_name}
                        onChange={(e) => setMemberFormData({ ...memberFormData, full_name: e.target.value })}
                        required
                        maxLength={100}
                      />
                    </div>
                    <div>
                      <Label htmlFor="member-email">Email *</Label>
                      <Input
                        id="member-email"
                        type="email"
                        value={memberFormData.email}
                        onChange={(e) => setMemberFormData({ ...memberFormData, email: e.target.value })}
                        required
                        maxLength={255}
                      />
                    </div>
                    <div>
                      <Label htmlFor="member-phone">Phone</Label>
                      <PhoneInput
                        id="member-phone"
                        value={memberFormData.phone}
                        onChange={(value) => setMemberFormData({ ...memberFormData, phone: value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="member-address">Address</Label>
                      <Input
                        id="member-address"
                        value={memberFormData.address}
                        onChange={(e) => setMemberFormData({ ...memberFormData, address: e.target.value })}
                        maxLength={500}
                      />
                    </div>
                    <div>
                      <Label htmlFor="member-dob">Date of Birth</Label>
                      <Input
                        id="member-dob"
                        type="date"
                        value={memberFormData.date_of_birth}
                        onChange={(e) => setMemberFormData({ ...memberFormData, date_of_birth: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="member-kids">Number of Children</Label>
                      <Input
                        id="member-kids"
                        type="number"
                        min="0"
                        max="50"
                        value={memberFormData.number_of_kids}
                        onChange={(e) => setMemberFormData({ ...memberFormData, number_of_kids: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsMemberFormDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit">
                        Complete Registration
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </TabsContent>

            <TabsContent value="bibleschool-gallery">
              <BibleSchoolGallery />
            </TabsContent>

            <TabsContent value="music">
              <MusicManagement />
            </TabsContent>

            <TabsContent value="youtube">
              <YouTubeVideosManagement />
            </TabsContent>

            <TabsContent value="members">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Member Management</h2>
                  <Button onClick={() => navigate('/member-portal')}>
                    <UserCog className="w-4 h-4 mr-2" />
                    Full Member Portal
                  </Button>
                </div>
                <MemberDirectory />
              </Card>
            </TabsContent>

            <TabsContent value="content">
              <Card className="p-6">
                <div className="flex flex-col gap-4 mb-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">
                      {language === 'en' ? 'Site Content Management' :
                       language === 'ru' ? 'Управление содержимым сайта' : 
                        'Gestión de Contenido del Sitio'}
                    </h2>
                    <Select value={selectedContentSection} onValueChange={setSelectedContentSection}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder={
                          language === 'en' ? 'All Sections' :
                          language === 'ru' ? 'Все разделы' :
                          'Todas las secciones'
                        } />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] overflow-y-auto">
                        <SelectItem value="all">
                          {language === 'en' ? 'All Sections' :
                           language === 'ru' ? 'Все разделы' :
                           'Todas las secciones'}
                        </SelectItem>
                        {availableSections.map((section) => (
                          <SelectItem key={section} value={section}>
                            {section.charAt(0).toUpperCase() + section.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex justify-end">
                    <Dialog open={isContentDialogOpen} onOpenChange={setIsContentDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={() => openContentDialog()}>
                          <Plus className="w-4 h-4 mr-2" />
                          {language === 'en' ? 'Add Content' :
                           language === 'ru' ? 'Добавить содержимое' :
                           'Agregar contenido'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>
                            {editingContent ? 
                              (language === 'en' ? 'Edit Content' :
                               language === 'ru' ? 'Редактировать содержимое' :
                               'Editar contenido') :
                              (language === 'en' ? 'Add New Content' :
                               language === 'ru' ? 'Добавить новое содержимое' :
                               'Agregar nuevo contenido')
                            }
                          </DialogTitle>
                          <DialogDescription>
                            {language === 'en' ? 'Manage website content across different languages' :
                             language === 'ru' ? 'Управление содержимым веб-сайта на разных языках' :
                             'Administrar contenido del sitio web en diferentes idiomas'}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="section">
                              {language === 'en' ? 'Section' :
                               language === 'ru' ? 'Раздел' :
                               'Sección'} *
                            </Label>
                            {editingContent ? (
                              <Input
                                id="section"
                                value={contentForm.section}
                                disabled
                                className="bg-muted"
                              />
                            ) : (
                              <>
                                <Select 
                                  value={isAddingNewSection ? "new" : contentForm.section} 
                                  onValueChange={(value) => {
                                    if (value === "new") {
                                      setIsAddingNewSection(true);
                                      setContentForm({ ...contentForm, section: "" });
                                    } else {
                                      setIsAddingNewSection(false);
                                      setContentForm({ ...contentForm, section: value });
                                    }
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={
                                      language === 'en' ? 'Select existing or add new' :
                                      language === 'ru' ? 'Выберите существующий или добавьте новый' :
                                      'Seleccionar existente o agregar nuevo'
                                    } />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[300px] overflow-y-auto">
                                    <SelectItem value="new">
                                      + {language === 'en' ? 'Add New Section' :
                                          language === 'ru' ? 'Добавить новый раздел' :
                                          'Agregar nueva sección'}
                                    </SelectItem>
                                    {availableSections.map((section) => (
                                      <SelectItem key={section} value={section}>
                                        {section.charAt(0).toUpperCase() + section.slice(1)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {isAddingNewSection && (
                                  <Input
                                    className="mt-2"
                                    value={customSection}
                                    onChange={(e) => setCustomSection(e.target.value)}
                                    placeholder={
                                      language === 'en' ? 'Enter new section name' :
                                      language === 'ru' ? 'Введите название нового раздела' :
                                      'Ingrese el nombre de la nueva sección'
                                    }
                                  />
                                )}
                              </>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="content_key">
                              {language === 'en' ? 'Content Key' :
                               language === 'ru' ? 'Ключ содержимого' :
                               'Clave de contenido'} *
                            </Label>
                            {editingContent ? (
                              <Input
                                id="content_key"
                                value={contentForm.content_key}
                                disabled
                                className="bg-muted"
                              />
                            ) : (
                              <>
                                {isAddingNewKey ? (
                                  <>
                                    <div className="flex items-center gap-2 mb-3">
                                      <input
                                        type="checkbox"
                                        id="bulleted-content"
                                        checked={isBulletedContent}
                                        onChange={(e) => setIsBulletedContent(e.target.checked)}
                                        className="rounded"
                                      />
                                      <Label htmlFor="bulleted-content" className="cursor-pointer">
                                        Create bulleted content structure
                                      </Label>
                                    </div>
                                    
                                    {!isBulletedContent ? (
                                      <Input
                                        value={customKey}
                                        onChange={(e) => setCustomKey(e.target.value)}
                                        placeholder="Enter key (e.g., bible.desc or services.time)"
                                      />
                                    ) : (
                                      <div className="space-y-3">
                                        <div>
                                          <Label htmlFor="bulleted-prefix">Base key prefix *</Label>
                                          <Input
                                            id="bulleted-prefix"
                                            value={bulletedPrefix}
                                            onChange={(e) => setBulletedPrefix(e.target.value)}
                                            placeholder="e.g., bulleted1"
                                          />
                                        </div>
                                        <div>
                                          <Label htmlFor="bulleted-heading">Heading *</Label>
                                          <Input
                                            id="bulleted-heading"
                                            value={bulletedHeading}
                                            onChange={(e) => setBulletedHeading(e.target.value)}
                                            placeholder="Heading text"
                                          />
                                        </div>
                                        <div>
                                          <Label>Text items *</Label>
                                          {bulletedTexts.map((text, index) => (
                                            <div key={index} className="flex gap-2 mb-2">
                                              <Input
                                                value={text}
                                                onChange={(e) => {
                                                  const newTexts = [...bulletedTexts];
                                                  newTexts[index] = e.target.value;
                                                  setBulletedTexts(newTexts);
                                                }}
                                                placeholder={`Text item ${index + 1}`}
                                              />
                                              {bulletedTexts.length > 1 && (
                                                <Button
                                                  type="button"
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={() => {
                                                    const newTexts = bulletedTexts.filter((_, i) => i !== index);
                                                    setBulletedTexts(newTexts);
                                                  }}
                                                >
                                                  <X className="h-4 w-4" />
                                                </Button>
                                              )}
                                            </div>
                                          ))}
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setBulletedTexts([...bulletedTexts, ""])}
                                          >
                                            <Plus className="h-4 w-4 mr-1" /> Add text item
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="mt-2"
                                      onClick={() => {
                                        setIsAddingNewKey(false);
                                        setCustomKey("");
                                        setIsBulletedContent(false);
                                        setBulletedPrefix("");
                                        setBulletedHeading("");
                                        setBulletedTexts([""]);
                                      }}
                                    >
                                      Select from existing
                                    </Button>
                                  </>
                                ) : (
                                   <div className="space-y-2">
                                    <Select 
                                      value={selectedKeySuffix} 
                                      onValueChange={setSelectedKeySuffix}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder={
                                          language === 'en' ? 'Select existing key' :
                                          language === 'ru' ? 'Выберите существующий ключ' :
                                          'Seleccionar clave existente'
                                        } />
                                      </SelectTrigger>
                                      <SelectContent className="max-h-[300px] overflow-y-auto">
                                        {keyGroups['section_keys']?.sort().map((key) => (
                                          <SelectItem key={key} value={key}>
                                            {key}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    
                                    {selectedKeySuffix && !isBulletedContent && (
                                      <div className="text-sm text-muted-foreground font-mono">
                                        Key: {selectedKeySuffix}
                                      </div>
                                    )}
                                    
                                    {/* Show bulleted content fields when a bulleted heading is selected */}
                                    {isBulletedContent && selectedKeySuffix && !isAddingNewKey && (
                                      <div className="space-y-3 mt-4 p-4 border rounded-lg bg-muted/30">
                                        {isLoadingBulletedContent ? (
                                          <p className="text-sm text-muted-foreground">Loading bulleted content...</p>
                                        ) : (
                                          <>
                                            <div className="text-sm font-medium text-primary">
                                              Editing: {bulletedPrefix}
                                            </div>
                                            <div>
                                              <Label htmlFor="edit-bulleted-heading">Heading *</Label>
                                              <Input
                                                id="edit-bulleted-heading"
                                                value={bulletedHeading}
                                                onChange={(e) => setBulletedHeading(e.target.value)}
                                                placeholder="Heading text"
                                              />
                                            </div>
                                            <div>
                                              <Label>Text items *</Label>
                                              {bulletedTexts.map((text, index) => (
                                                <div key={index} className="flex gap-2 mb-2">
                                                  <Input
                                                    value={text}
                                                    onChange={(e) => {
                                                      const newTexts = [...bulletedTexts];
                                                      newTexts[index] = e.target.value;
                                                      setBulletedTexts(newTexts);
                                                    }}
                                                    placeholder={`Text item ${index + 1}`}
                                                  />
                                                  {bulletedTexts.length > 1 && (
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      variant="ghost"
                                                      onClick={() => {
                                                        const newTexts = bulletedTexts.filter((_, i) => i !== index);
                                                        setBulletedTexts(newTexts);
                                                      }}
                                                    >
                                                      <X className="h-4 w-4" />
                                                    </Button>
                                                  )}
                                                </div>
                                              ))}
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setBulletedTexts([...bulletedTexts, ""])}
                                              >
                                                <Plus className="h-4 w-4 mr-1" /> Add text item
                                              </Button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}
                                    
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setIsAddingNewKey(true);
                                        setSelectedKeyGroup("");
                                        setSelectedKeySuffix("");
                                      }}
                                    >
                                      + Add New Key
                                    </Button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="language">Language *</Label>
                            <Select value={contentForm.language} onValueChange={(value) => setContentForm({ ...contentForm, language: value })}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="en">English</SelectItem>
                                <SelectItem value="es">Spanish</SelectItem>
                                <SelectItem value="ru">Russian</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Hide regular content textarea when editing bulleted content via dropdown */}
                          {!(isBulletedContent && selectedKeySuffix && !isAddingNewKey) && (
                            <div>
                              <Label htmlFor="content_value">Content *</Label>
                              <Textarea
                                id="content_value"
                                value={contentForm.content_value}
                                onChange={(e) => setContentForm({ ...contentForm, content_value: e.target.value })}
                                rows={5}
                                required
                              />
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsContentDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="button" onClick={handleSaveContent}>
                            Save
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {isLoadingContent ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Loading content...</p>
                  </div>
                ) : Object.keys(groupedSections).length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No content found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-semibold">
                            {language === 'en' ? 'Section' :
                             language === 'ru' ? 'Раздел' :
                             'Sección'}
                          </th>
                          <th className="text-left p-3 font-semibold">
                            {language === 'en' ? 'Fields' :
                             language === 'ru' ? 'Поля' :
                             'Campos'}
                          </th>
                          <th className="text-center p-3 font-semibold">
                            {language === 'en' ? 'Actions' :
                             language === 'ru' ? 'Действия' :
                             'Acciones'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(groupedSections).map(([sectionName, fields]) => (
                          <tr 
                            key={sectionName} 
                            className="border-b hover:bg-muted/50 cursor-pointer"
                            onClick={() => openSectionFieldsDialog(sectionName)}
                          >
                            <td className="p-3">
                              <span className="inline-flex items-center px-3 py-2 rounded text-sm font-medium bg-primary/10 text-primary">
                                {sectionName.charAt(0).toUpperCase() + sectionName.slice(1)}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className="text-sm text-muted-foreground">
                                {fields.length} {language === 'en' ? 'fields' :
                                                 language === 'ru' ? 'полей' :
                                                 'campos'}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex gap-2 justify-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openSectionFieldsDialog(sectionName);
                                    }}
                                  >
                                    <Edit className="w-4 h-4 mr-1" />
                                    {language === 'en' ? 'Edit Fields' :
                                     language === 'ru' ? 'Редактировать поля' :
                                     'Editar campos'}
                                  </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Section Fields Dialog */}
                <Dialog open={isSectionFieldsDialogOpen} onOpenChange={setIsSectionFieldsDialogOpen}>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {language === 'en' ? 'Edit' :
                         language === 'ru' ? 'Редактировать' :
                         'Editar'} {editingSectionName.charAt(0).toUpperCase() + editingSectionName.slice(1)} {
                          language === 'en' ? 'Section' :
                          language === 'ru' ? 'Раздел' :
                          'Sección'
                        } - {language.toUpperCase()}
                      </DialogTitle>
                      <DialogDescription>
                        {language === 'en' ? 'Update all fields for this section in English' :
                         language === 'ru' ? 'Обновить все поля для этого раздела на русском языке' :
                         'Actualizar todos los campos de esta sección en español'}
                        {language !== 'en' && (
                          <span className="block mt-1 text-xs text-muted-foreground">
                            {language === 'ru' ? 
                              '(Поля с меткой [EN] используют английский текст. Отредактируйте их для добавления перевода)' :
                              '(Los campos marcados con [EN] usan texto en inglés. Edítelos para agregar traducción)'}
                          </span>
                        )}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6">
                      {(() => {
                        // Group fields by their prefix (e.g., "sunday", "prayer", "bible", "about", "gatherings")
                        const fieldGroups: Record<string, typeof selectedSectionFields> = {};
                        
                        selectedSectionFields.forEach(field => {
                          // Try splitting by dot first, then underscore
                          let parts = field.content_key.split('.');
                          let prefix = 'general';
                          
                          if (parts.length > 1) {
                            // Has dot separator
                            prefix = parts[0];
                          } else {
                            // Try underscore separator
                            parts = field.content_key.split('_');
                            if (parts.length > 1) {
                              // Has underscore separator - use first part as prefix
                              prefix = parts[0];
                            }
                          }
                          
                          if (!fieldGroups[prefix]) {
                            fieldGroups[prefix] = [];
                          }
                          fieldGroups[prefix].push(field);
                        });
                        
                        // Sort fields within each group to show title field first
                        Object.keys(fieldGroups).forEach(groupName => {
                          fieldGroups[groupName].sort((a, b) => {
                            // Field matching the group name (without dot) should come first
                            const aIsTitle = a.content_key === groupName;
                            const bIsTitle = b.content_key === groupName;
                            
                            if (aIsTitle && !bIsTitle) return -1;
                            if (!aIsTitle && bIsTitle) return 1;
                            
                            // Otherwise maintain alphabetical order
                            return a.content_key.localeCompare(b.content_key);
                          });
                        });
                        
                        return Object.entries(fieldGroups).map(([groupName, fields]) => (
                          <div key={groupName} className="space-y-3 border rounded-lg p-4 bg-muted/30">
                            <h3 className="text-lg font-semibold capitalize text-primary">
                              {groupName}
                            </h3>
                            <div className="space-y-3 pl-2">
                              {fields.map((field) => {
                                const fieldSuffix = field.content_key.includes('.') 
                                  ? field.content_key.split('.').slice(1).join('.') 
                                  : field.content_key;
                                
                                return (
                                  <div key={field.id} className="space-y-2 border rounded-lg p-3 bg-background relative">
                                    <div className="flex items-start justify-between gap-2">
                                      <Label htmlFor={field.id} className="font-mono text-sm text-muted-foreground">
                                        {fieldSuffix}
                                      </Label>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="text-destructive hover:text-destructive hover:bg-destructive/10 h-6 w-6 p-0"
                                        onClick={() => handleDeleteSectionField(field.id)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                    
                                    {customFieldValues[field.id] ? (
                                      <div className="space-y-2">
                                        <Textarea
                                          id={field.id}
                                          value={field.content_value}
                                          onChange={(e) => handleUpdateSectionField(field.id, e.target.value)}
                                          rows={3}
                                          className="w-full"
                                        />
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          onClick={() => toggleCustomFieldValue(field.id)}
                                        >
                                          Select from existing
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        <Select
                                          value={field.content_value}
                                          onValueChange={(value) => {
                                            if (value === "custom") {
                                              toggleCustomFieldValue(field.id);
                                            } else {
                                              handleUpdateSectionField(field.id, value);
                                            }
                                          }}
                                        >
                                          <SelectTrigger>
                                            <SelectValue placeholder="Select or enter custom" />
                                          </SelectTrigger>
                                          <SelectContent className="max-h-[200px] overflow-y-auto">
                                            <SelectItem value="custom">+ Enter Custom Value</SelectItem>
                                            {fieldValueOptions[field.content_key]?.map((value, idx) => (
                                              <SelectItem key={idx} value={value}>
                                                {value.length > 50 ? value.substring(0, 50) + "..." : value}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <div className="text-xs text-muted-foreground line-clamp-2">
                                          {field.content_value}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    {isSaving && (
                      <div className="space-y-2 px-6 pb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{savingMessage}</span>
                          <span className="font-medium">{Math.round(savingProgress)}%</span>
                        </div>
                        <Progress value={savingProgress} className="h-2" />
                      </div>
                    )}
                    <DialogFooter>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsSectionFieldsDialogOpen(false)}
                        disabled={isSaving}
                      >
                        {language === 'en' ? 'Cancel' :
                         language === 'ru' ? 'Отмена' :
                         'Cancelar'}
                      </Button>
                      <Button 
                        type="button" 
                        onClick={handleSaveSectionFields}
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          language === 'en' ? 'Saving...' :
                          language === 'ru' ? 'Сохранение...' :
                          'Guardando...'
                        ) : (
                          language === 'en' ? 'Save All Changes' :
                          language === 'ru' ? 'Сохранить все изменения' :
                          'Guardar todos los cambios'
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </Card>
            </TabsContent>

            <TabsContent value="announcements">
              <AnnouncementsManagement />
            </TabsContent>

            <TabsContent value="sermons">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">{t("admin.sermons.title")}</h2>
                  {(isAdmin || isSermonEditor) && (
                    <Dialog open={isSermonDialogOpen} onOpenChange={setIsSermonDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={() => handleCloseSermonDialog()}>
                          <Plus className="w-4 h-4 mr-2" />
                          {t("admin.sermons.addsermon")}
                        </Button>
                      </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>
                          {editingSermon ? t("admin.sermons.editsermon") : t("admin.sermons.addnew")}
                        </DialogTitle>
                        <DialogDescription>
                          {t("admin.sermons.filldetails")}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleSermonSubmit} className="space-y-4">
                        <div>
                          <Label htmlFor="title">{t("admin.sermons.title.label")} *</Label>
                          <Input
                            id="title"
                            value={sermonForm.title}
                            onChange={(e) => setSermonForm({ ...sermonForm, title: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="speaker">{t("admin.sermons.speaker.label")} *</Label>
                          <Input
                            id="speaker"
                            value={sermonForm.speaker}
                            onChange={(e) => setSermonForm({ ...sermonForm, speaker: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="sermon_date">{t("admin.sermons.date.label")} *</Label>
                          <Input
                            id="sermon_date"
                            type="date"
                            value={sermonForm.sermon_date}
                            onChange={(e) => setSermonForm({ ...sermonForm, sermon_date: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="scripture_reference">{t("admin.sermons.scripture.label")}</Label>
                          <Textarea
                            id="scripture_reference"
                            value={sermonForm.scripture_reference}
                            onChange={(e) => setSermonForm({ ...sermonForm, scripture_reference: e.target.value })}
                            placeholder={t("admin.sermons.scripture.placeholder")}
                            rows={3}
                          />
                        </div>
                        <div>
                          <Label htmlFor="description">{t("admin.sermons.description.label")}</Label>
                          <Textarea
                            id="description"
                            value={sermonForm.description}
                            onChange={(e) => setSermonForm({ ...sermonForm, description: e.target.value })}
                            rows={4}
                          />
                        </div>
                        <div>
                          <Label htmlFor="audio_url">{t("admin.sermons.audiourl.label")}</Label>
                          <Input
                            id="audio_url"
                            type="url"
                            value={sermonForm.audio_url}
                            onChange={(e) => setSermonForm({ ...sermonForm, audio_url: e.target.value })}
                            placeholder={t("admin.sermons.audiourl.placeholder")}
                          />
                        </div>
                        <div>
                          <Label htmlFor="video_url">{t("admin.sermons.videourl.label")}</Label>
                          <Input
                            id="video_url"
                            type="url"
                            value={sermonForm.video_url}
                            onChange={(e) => setSermonForm({ ...sermonForm, video_url: e.target.value })}
                            placeholder={t("admin.sermons.videourl.placeholder")}
                          />
                        </div>
                        {isAdmin && (
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="published"
                              checked={sermonForm.published}
                              onChange={(e) => setSermonForm({ ...sermonForm, published: e.target.checked })}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="published">{t("admin.sermons.published")}</Label>
                          </div>
                        )}
                        <DialogFooter>
                          {sermonSavingProgress > 0 && (
                            <div className="w-full mb-4">
                              <Progress value={sermonSavingProgress} className="w-full" />
                              <p className="text-xs text-muted-foreground text-center mt-1">
                                {sermonSavingProgress < 40 ? 'Saving...' : 
                                 sermonSavingProgress < 90 ? 'Translating...' : 'Finalizing...'}
                              </p>
                            </div>
                          )}
                          <Button type="button" variant="outline" onClick={handleCloseSermonDialog} disabled={sermonSavingProgress > 0}>
                            {t("admin.sermons.cancel")}
                          </Button>
                          <Button type="submit" disabled={sermonSavingProgress > 0}>
                            {editingSermon ? t("admin.sermons.update") : t("admin.sermons.create")}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  )}
                </div>

                {isLoadingSermons ? (
                  <p className="text-center py-8">{t("admin.sermons.loading")}</p>
                ) : sermons.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">
                    {t("admin.sermons.nosermons")}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">{t("admin.sermons.title.label")}</th>
                          <th className="text-left p-3">{t("admin.sermons.speaker.label")}</th>
                          <th className="text-left p-3">{t("admin.sermons.date.label")}</th>
                          <th className="text-left p-3">{t("admin.sermons.scripture.label")}</th>
                          <th className="text-left p-3">{t("admin.sermons.media")}</th>
                          <th className="text-center p-3">{t("admin.donations.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sermons.map((sermon) => (
                          <tr key={sermon.id} className="border-b hover:bg-muted/50">
                            <td className="p-3">{getSermonDisplayText(sermon, 'title')}</td>
                            <td className="p-3">{getSermonDisplayText(sermon, 'speaker')}</td>
                            <td className="p-3">
                              {formatDateForDisplay(sermon.sermon_date)}
                            </td>
                            <td className="p-3">{getSermonDisplayText(sermon, 'scripture_reference') || "-"}</td>
                            <td className="p-3">
                              <div className="flex gap-1">
                                {sermon.audio_url && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{t("admin.sermons.mediatype.audio")}</span>
                                )}
                                {sermon.video_url && (
                                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">{t("admin.sermons.mediatype.video")}</span>
                                )}
                                {!sermon.audio_url && !sermon.video_url && "-"}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-2">
                                {(isAdmin || (isSermonEditor && sermon.created_by === user?.id && !sermon.published)) && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleEditSermon(sermon)}
                                      title={t("admin.sermons.editsermon")}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    {isAdmin && (
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleManageTranslations(sermon)}
                                        title={t("admin.sermons.translations")}
                                      >
                                        <Languages className="h-4 w-4" />
                                      </Button>
                                    )}
                                    <Button
                                      variant="destructive"
                                      size="icon"
                                      onClick={() => handleDeleteSermon(sermon.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="blog">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">{t("admin.blog.title")}</h2>
                  {(isAdmin || isBlogEditor) && (
                    <Dialog open={isBlogDialogOpen} onOpenChange={setIsBlogDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={() => {
                          setEditingBlog(null);
                          setBlogForm({
                            title: "",
                            slug: "",
                            author: "",
                            category: "",
                            excerpt: "",
                            content: "",
                            featured_image_url: "",
                            published: false,
                          });
                          setIsSlugManuallyEdited(false);
                        }}>
                          <Plus className="w-4 h-4 mr-2" />
                          {t("admin.blog.add")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{editingBlog ? t("admin.blog.edit") : t("admin.blog.create")}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleBlogSubmit} className="space-y-4">
                           <div>
                            <Label htmlFor="blog-title">{t("admin.blog.field.title")} *</Label>
                            <Input
                              id="blog-title"
                              value={blogForm.title}
                              onChange={(e) => {
                                const newTitle = e.target.value;
                                setBlogForm({ 
                                  ...blogForm, 
                                  title: newTitle,
                                  slug: isSlugManuallyEdited ? blogForm.slug : generateSlug(newTitle)
                                });
                              }}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="blog-slug">{t("admin.blog.field.slug")}</Label>
                            <Input
                              id="blog-slug"
                              value={blogForm.slug}
                              onChange={(e) => {
                                setBlogForm({ ...blogForm, slug: e.target.value });
                                setIsSlugManuallyEdited(true);
                              }}
                              placeholder={t("admin.blog.field.slug.placeholder")}
                            />
                          </div>
                          <div>
                            <Label htmlFor="blog-author">{t("admin.blog.field.author")} *</Label>
                            <Input
                              id="blog-author"
                              value={blogForm.author}
                              onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="blog-category">{t("admin.blog.field.category")} *</Label>
                            <Input
                              id="blog-category"
                              value={blogForm.category}
                              onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="blog-excerpt">{t("admin.blog.field.excerpt")}</Label>
                            <Textarea
                              id="blog-excerpt"
                              value={blogForm.excerpt}
                              onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                              rows={2}
                            />
                          </div>
                          <div>
                            <Label htmlFor="blog-content">{t("admin.blog.field.content")} *</Label>
                            <Textarea
                              id="blog-content"
                              value={blogForm.content}
                              onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                              rows={8}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="blog-image">{t("admin.blog.field.image")}</Label>
                            <Input
                              id="blog-image"
                              type="url"
                              value={blogForm.featured_image_url}
                              onChange={(e) => setBlogForm({ ...blogForm, featured_image_url: e.target.value })}
                            />
                          </div>
                          {isAdmin ? (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="blog-published"
                                  checked={blogForm.published}
                                  onChange={(e) => setBlogForm({ ...blogForm, published: e.target.checked })}
                                  className="w-4 h-4"
                                />
                                <Label htmlFor="blog-published" className="font-semibold">
                                  {t("admin.blog.field.published")}
                                </Label>
                              </div>
                              {!blogForm.published && (
                                <p className="text-xs text-muted-foreground">
                                  Check this box to publish the post and make it visible to everyone
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                              Your post will be saved as a draft for admin review
                            </div>
                          )}
                          <DialogFooter>
                            {blogSavingProgress > 0 && (
                              <div className="w-full mb-4">
                                <Progress value={blogSavingProgress} className="w-full" />
                                <p className="text-xs text-muted-foreground text-center mt-1">
                                  {blogSavingProgress < 40 ? 'Saving...' : 
                                   blogSavingProgress < 90 ? 'Translating...' : 'Finalizing...'}
                                </p>
                              </div>
                            )}
                            <Button type="button" variant="outline" onClick={() => setIsBlogDialogOpen(false)} disabled={blogSavingProgress > 0}>
                              {t("admin.blog.cancel")}
                            </Button>
                            <Button type="submit" disabled={blogSavingProgress > 0}>
                              {editingBlog ? t("admin.blog.update") : t("admin.blog.create")}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>

                {isLoadingBlogs ? (
                  <p className="text-center py-8">{t("admin.blog.loading")}</p>
                ) : (
                  <>
                    {/* Status Filter Buttons */}
                    <div className="flex gap-2 mb-4">
                      <Button
                        variant={blogStatusFilter === "all" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setBlogStatusFilter("all")}
                      >
                        {t("admin.blog.filter.all")} ({blogPosts.length})
                      </Button>
                      <Button
                        variant={blogStatusFilter === "published" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setBlogStatusFilter("published")}
                      >
                        {t("admin.blog.filter.published")} ({blogPosts.filter(b => b.published).length})
                      </Button>
                      <Button
                        variant={blogStatusFilter === "draft" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setBlogStatusFilter("draft")}
                      >
                        {t("admin.blog.filter.draft")} ({blogPosts.filter(b => !b.published).length})
                      </Button>
                    </div>

                    {filteredBlogPosts.length === 0 ? (
                      <p className="text-center py-8 text-muted-foreground">{t("admin.blog.noposts")}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3">{t("admin.blog.field.title")}</th>
                              <th className="text-left p-3">{t("admin.blog.field.author")}</th>
                              <th className="text-left p-3">{t("admin.blog.field.category")}</th>
                              <th className="text-left p-3">{t("admin.blog.status")}</th>
                              <th className="text-center p-3">{t("admin.blog.actions")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBlogPosts.map((blog) => {
                          const translation = blog.translations?.[language];
                          const displayTitle = translation?.title || blog.title;
                          const displayAuthor = translation?.author || blog.author;
                          
                          return (
                          <tr key={blog.id} className="border-b hover:bg-muted/50">
                            <td className="p-3">{displayTitle}</td>
                            <td className="p-3">{displayAuthor}</td>
                            <td className="p-3">{blog.category}</td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-1 rounded ${
                                blog.published 
                                  ? "bg-green-100 text-green-800" 
                                  : "bg-gray-100 text-gray-800"
                              }`}>
                                {blog.published ? t("admin.blog.status.published") : t("admin.blog.status.draft")}
                              </span>
                            </td>
                             <td className="p-3">
                              <div className="flex items-center justify-center gap-2">
                                {(isAdmin || (isBlogEditor && blog.created_by === user?.id && !blog.published)) && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleEditBlog(blog)}
                                      title={t("admin.blog.edit")}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleManageBlogTranslations(blog)}
                                      title={t("admin.blog.translations")}
                                    >
                                      <Languages className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="icon"
                                      onClick={() => handleDeleteBlog(blog.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="bibleschool">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">{t("admin.bibleschool.title")}</h2>
                  {(isAdmin || isBibleSchoolEditor) && (
                     <Dialog open={isCourseDialogOpen} onOpenChange={setIsCourseDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={() => {
                          setEditingCourse(null);
                          setCourseForm({
                            title: "",
                            description: "",
                            instructor: "",
                            duration: "",
                            level: "",
                            schedule: "",
                            max_students: "",
                            image_url: "",
                            published: isAdmin,
                          });
                        }}>
                          <Plus className="w-4 h-4 mr-2" />
                          {t("admin.bibleschool.add")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{editingCourse ? t("admin.bibleschool.edit") : t("admin.bibleschool.create")}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCourseSubmit} className="space-y-4">
                          <div>
                            <Label htmlFor="course-title">{t("admin.bibleschool.field.title")} *</Label>
                            <Input
                              id="course-title"
                              value={courseForm.title}
                              onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="course-description">{t("admin.bibleschool.field.description")} *</Label>
                            <Textarea
                              id="course-description"
                              value={courseForm.description}
                              onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                              rows={4}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="course-instructor">{t("admin.bibleschool.field.instructor")} *</Label>
                            <Input
                              id="course-instructor"
                              value={courseForm.instructor}
                              onChange={(e) => setCourseForm({ ...courseForm, instructor: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="course-duration">{t("admin.bibleschool.field.duration")} *</Label>
                            <Input
                              id="course-duration"
                              value={courseForm.duration}
                              onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
                              placeholder={t("admin.bibleschool.field.duration.placeholder")}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="course-level">{t("admin.bibleschool.field.level")} *</Label>
                            <Input
                              id="course-level"
                              value={courseForm.level}
                              onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value })}
                              placeholder={t("admin.bibleschool.field.level.placeholder")}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="course-schedule">{t("admin.bibleschool.field.schedule")}</Label>
                            <Input
                              id="course-schedule"
                              value={courseForm.schedule}
                              onChange={(e) => setCourseForm({ ...courseForm, schedule: e.target.value })}
                              placeholder={t("admin.bibleschool.field.schedule.placeholder")}
                            />
                          </div>
                          <div>
                            <Label htmlFor="course-max">{t("admin.bibleschool.field.maxstudents")}</Label>
                            <Input
                              id="course-max"
                              type="number"
                              value={courseForm.max_students}
                              onChange={(e) => setCourseForm({ ...courseForm, max_students: e.target.value })}
                            />
                          </div>
                          <div>
                            <Label htmlFor="course-image">{t("admin.bibleschool.field.image")}</Label>
                            <Input
                              id="course-image"
                              type="url"
                              value={courseForm.image_url}
                              onChange={(e) => setCourseForm({ ...courseForm, image_url: e.target.value })}
                            />
                          </div>
                          {isAdmin ? (
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id="course-published"
                                  checked={courseForm.published}
                                  onChange={(e) => setCourseForm({ ...courseForm, published: e.target.checked })}
                                  className="w-4 h-4"
                                />
                                <Label htmlFor="course-published" className="font-semibold">
                                  {t("admin.bibleschool.field.published")}
                                </Label>
                              </div>
                              {!courseForm.published && (
                                <p className="text-xs text-muted-foreground">
                                  Check this box to publish the course and make it visible to everyone
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                              Your course will be saved as a draft for admin review
                            </div>
                          )}
                          <DialogFooter>
                            {courseSavingProgress > 0 && (
                              <div className="w-full mb-4">
                                <Progress value={courseSavingProgress} className="w-full" />
                                <p className="text-xs text-muted-foreground text-center mt-1">
                                  {courseSavingProgress < 40 ? 'Saving...' : 
                                   courseSavingProgress < 90 ? 'Translating...' : 'Finalizing...'}
                                </p>
                              </div>
                            )}
                            <Button type="button" variant="outline" onClick={() => setIsCourseDialogOpen(false)} disabled={courseSavingProgress > 0}>
                              {t("admin.bibleschool.cancel")}
                            </Button>
                            <Button type="submit" disabled={courseSavingProgress > 0}>
                              {editingCourse ? t("admin.bibleschool.update") : t("admin.bibleschool.create")}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>

                {isLoadingCourses ? (
                  <p className="text-center py-8">{t("admin.bibleschool.loading")}</p>
                ) : (
                  <>
                    {/* Status Filter Buttons */}
                    <div className="flex gap-2 mb-4">
                      <Button
                        variant={courseStatusFilter === "all" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCourseStatusFilter("all")}
                      >
                        {t("admin.bibleschool.filter.all")} ({courses.length})
                      </Button>
                      <Button
                        variant={courseStatusFilter === "published" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCourseStatusFilter("published")}
                      >
                        {t("admin.bibleschool.filter.published")} ({courses.filter(c => c.published).length})
                      </Button>
                      <Button
                        variant={courseStatusFilter === "draft" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCourseStatusFilter("draft")}
                      >
                        {t("admin.bibleschool.filter.draft")} ({courses.filter(c => !c.published).length})
                      </Button>
                    </div>

                    {filteredCourses.length === 0 ? (
                      <p className="text-center py-8 text-muted-foreground">{t("admin.bibleschool.nocourses")}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                        <tr className="border-b">
                          <th className="text-left p-3">{t("admin.bibleschool.field.title")}</th>
                          <th className="text-left p-3">{t("admin.bibleschool.field.description")}</th>
                          <th className="text-left p-3">{t("admin.bibleschool.field.instructor")}</th>
                          <th className="text-left p-3">{t("admin.bibleschool.field.duration")}</th>
                          <th className="text-left p-3">{t("admin.bibleschool.field.level")}</th>
                          <th className="text-left p-3">{t("admin.bibleschool.field.schedule")}</th>
                          <th className="text-left p-3">{t("admin.bibleschool.field.maxstudents")}</th>
                          <th className="text-left p-3">{t("admin.bibleschool.field.image")}</th>
                          <th className="text-left p-3">{t("admin.bibleschool.status")}</th>
                          <th className="text-center p-3">{t("admin.bibleschool.actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCourses.map((course) => {
                          const translation = course.translations?.[language];
                          const displayTitle = translation?.title || course.title;
                          const displayDescription = translation?.description || course.description;
                          const displayInstructor = translation?.instructor || course.instructor;
                          const displayDuration = translation?.duration || course.duration;
                          const displayLevel = translation?.level || course.level;
                          const displaySchedule = translation?.schedule || course.schedule;
                          
                          return (
                            <tr key={course.id} className="border-b hover:bg-muted/50">
                              <td className="p-3">{displayTitle}</td>
                              <td className="p-3">
                                <div className="max-w-xs truncate" title={displayDescription}>
                                  {displayDescription}
                                </div>
                              </td>
                              <td className="p-3">{displayInstructor}</td>
                              <td className="p-3">{displayDuration}</td>
                              <td className="p-3">{displayLevel}</td>
                              <td className="p-3">{displaySchedule || '-'}</td>
                            <td className="p-3">{course.max_students || '-'}</td>
                            <td className="p-3">{course.image_url ? '✓' : '-'}</td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-1 rounded ${
                                course.published 
                                  ? "bg-green-100 text-green-800" 
                                  : "bg-gray-100 text-gray-800"
                              }`}>
                                {course.published ? t("admin.bibleschool.status.published") : t("admin.bibleschool.status.draft")}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-2">
                                {(isAdmin || (isBibleSchoolEditor && course.created_by === user?.id && !course.published)) && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleEditCourse(course)}
                                      title={t("admin.bibleschool.edit")}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => handleManageCourseTranslations(course)}
                                      title={t("admin.bibleschool.translations")}
                                    >
                                      <Languages className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() => {
                                        setManagingCourseId(course.id);
                                        setIsCourseDocumentsDialogOpen(true);
                                      }}
                                      title="Manage Documents"
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="icon"
                                      onClick={() => handleDeleteCourse(course.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                    )}
                  </>
                )}
              </Card>
            </TabsContent>

            {/* Course Documents Dialog */}
            <Dialog open={isCourseDocumentsDialogOpen} onOpenChange={setIsCourseDocumentsDialogOpen}>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Manage Course Documents</DialogTitle>
                  <DialogDescription>
                    Upload and manage PDF documents for this course
                  </DialogDescription>
                </DialogHeader>
                {managingCourseId && <CourseDocuments courseId={managingCourseId} />}
              </DialogContent>
            </Dialog>

            <TabsContent value="photos">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">{t("admin.photos.title")}</h2>
                    <Select value={selectedMission} onValueChange={(value: any) => {
                      setSelectedMission(value);
                      setMissionTrips([]);
                      setMissionPhotos([]);
                      setSelectedTrip(null);
                      setSelectedPhotos([]);
                      setIsSelectMode(false);
                    }}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="guatemala">{t("admin.category.guatemala")}</SelectItem>
                        <SelectItem value="mexico">{t("admin.category.mexico")}</SelectItem>
                        <SelectItem value="cuba">{t("admin.category.cuba")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {isAdmin && (
                    <Button onClick={() => openTripDialog()}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create New Trip
                    </Button>
                  )}
                </div>

                {isLoadingTrips ? (
                  <p className="text-center py-8">Loading trips...</p>
                ) : missionTrips.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No trips yet. Create your first trip!</p>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {missionTrips.map((trip) => (
                        <Card 
                          key={trip.id} 
                          className={`cursor-pointer hover:shadow-lg transition-shadow ${
                            selectedTrip === trip.id ? 'ring-2 ring-primary' : ''
                          }`}
                          onClick={() => {
                            setSelectedTrip(trip.id);
                            fetchTripPhotos(trip.id);
                            setSelectedPhotos([]);
                            setIsSelectMode(false);
                          }}
                        >
                          <div className="p-4">
                            <h3 className="font-semibold text-lg mb-2">{trip.title}</h3>
                            {trip.description && (
                              <p className="text-sm text-muted-foreground mb-2">{trip.description}</p>
                            )}
                            {trip.trip_date && (
                              <p className="text-xs text-muted-foreground mb-3">
                                Date: {formatDateForDisplay(trip.trip_date)}
                              </p>
                            )}
                            {isAdmin && (
                              <div className="flex gap-2 mt-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTripDialog(trip);
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTrip(trip.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>

                    {selectedTrip && (
                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold">Trip Media Gallery</h3>
                          {isAdmin && (
                            <div className="flex gap-2 flex-wrap">
                              <Button 
                                onClick={() => {
                                  setIsSelectMode(!isSelectMode);
                                  setSelectedPhotos([]);
                                }} 
                                variant="outline"
                                size="sm"
                              >
                                {isSelectMode ? "Cancel Selection" : "Select Multiple"}
                              </Button>
                              {isSelectMode && (
                                <>
                                  <Button onClick={selectAllPhotos} variant="outline" size="sm">
                                    Select All
                                  </Button>
                                  <Button onClick={deselectAllPhotos} variant="outline" size="sm">
                                    Deselect All
                                  </Button>
                                  <Button 
                                    onClick={handleBulkDelete} 
                                    variant="destructive"
                                    size="sm"
                                    disabled={selectedPhotos.length === 0}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete ({selectedPhotos.length})
                                  </Button>
                                </>
                              )}
                              <Dialog open={isPhotoDialogOpen} onOpenChange={setIsPhotoDialogOpen}>
                                <DialogTrigger asChild>
                                  <Button onClick={() => openPhotoDialog()} size="sm">
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload Media
                                  </Button>
                                </DialogTrigger>
                              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>
                                    {editingPhoto ? t("admin.photos.edit") : "Upload Photos & Videos"}
                                  </DialogTitle>
                                  <DialogDescription>
                                    {editingPhoto 
                                      ? t("admin.photos.description")
                                      : "Select multiple images and/or videos to upload to the gallery. Supported formats: JPG, PNG, WebP, MP4, MOV, AVI, WebM"}
                                  </DialogDescription>
                                </DialogHeader>
                                <form onSubmit={editingPhoto ? handleEditPhoto : handlePhotoUpload} className="space-y-4">
                                  {!editingPhoto && (
                                    <>
                                      <div className="space-y-2">
                                        <Label htmlFor="photo-video-url" className="text-base font-semibold">
                                          YouTube Video URL (Optional)
                                        </Label>
                                        <Input
                                          id="photo-video-url"
                                          type="url"
                                          value={photoForm.video_url}
                                          onChange={(e) => setPhotoForm({ ...photoForm, video_url: e.target.value })}
                                          placeholder="https://www.youtube.com/watch?v=..."
                                        />
                                        <p className="text-xs text-muted-foreground">
                                          Enter a YouTube URL to embed a video instead of uploading files
                                        </p>
                                      </div>
                                      
                                      {!photoForm.video_url && (
                                        <div className="space-y-2">
                                          <div className="relative">
                                            <div className="absolute inset-0 flex items-center">
                                              <span className="w-full border-t" />
                                            </div>
                                            <div className="relative flex justify-center text-xs uppercase">
                                              <span className="bg-background px-2 text-muted-foreground">
                                                Or upload files
                                              </span>
                                            </div>
                                          </div>
                                          
                                          <Label htmlFor="photo-file" className="text-base font-semibold">
                                            Select Files (Images & Videos)
                                          </Label>
                                          <Input
                                            id="photo-file"
                                            type="file"
                                            accept="image/*,video/*"
                                            multiple
                                            onChange={(e) => {
                                              const files = Array.from(e.target.files || []);
                                              setUploadingFiles(files);
                                            }}
                                            className="cursor-pointer"
                                          />
                                          <p className="text-xs text-muted-foreground">
                                            💡 Tip: Hold Ctrl (Windows) or Cmd (Mac) to select multiple files
                                          </p>
                                          {uploadingFiles.length > 0 && (
                                            <div className="mt-3 p-4 bg-accent/50 border-2 border-primary/20 rounded-lg">
                                              <p className="text-sm font-semibold mb-2 text-foreground">
                                                {uploadingFiles.length} file{uploadingFiles.length > 1 ? 's' : ''} selected:
                                              </p>
                                              <ul className="text-sm space-y-2 max-h-32 overflow-y-auto">
                                                {uploadingFiles.map((file, idx) => (
                                                  <li key={idx} className="flex items-center gap-2 text-foreground">
                                                    <span className="text-base">{file.type.startsWith('video/') ? '🎥' : '🖼️'}</span>
                                                    <span className="font-medium">{file.name}</span>
                                                  </li>
                                                ))}
                                              </ul>
                                              
                                              {uploadProgress > 0 && (
                                                <div className="mt-4 space-y-2">
                                                  <div className="flex items-center justify-between text-sm">
                                                    <span className="text-foreground font-medium">
                                                      Uploading: {currentUploadingFile}
                                                    </span>
                                                    <span className="text-muted-foreground">
                                                      {Math.round(uploadProgress)}%
                                                    </span>
                                                  </div>
                                                  <Progress value={uploadProgress} className="h-2" />
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )}
                                  <div>
                                    <Label htmlFor="photo-title">{t("admin.photos.phototitle")} {!editingPhoto && (photoForm.video_url || uploadingFiles.length <= 1) && '*'}</Label>
                                    <Input
                                      id="photo-title"
                                      value={photoForm.title}
                                      onChange={(e) => setPhotoForm({ ...photoForm, title: e.target.value })}
                                      required={!!editingPhoto || !!photoForm.video_url || uploadingFiles.length <= 1}
                                      placeholder={uploadingFiles.length > 1 && !photoForm.video_url ? "Optional - will use filenames if empty" : ""}
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="photo-description">{t("admin.photos.photodescription")}</Label>
                                    <Textarea
                                      id="photo-description"
                                      value={photoForm.description}
                                      onChange={(e) => setPhotoForm({ ...photoForm, description: e.target.value })}
                                      rows={3}
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="display-order">{t("admin.photos.displayorder")}</Label>
                                    <Input
                                      id="display-order"
                                      type="number"
                                      value={photoForm.display_order}
                                      onChange={(e) => setPhotoForm({ ...photoForm, display_order: parseInt(e.target.value) || 0 })}
                                    />
                                  </div>
                                  <DialogFooter>
                                    <Button type="button" variant="outline" onClick={closePhotoDialog}>
                                      {t("admin.photos.cancel")}
                                    </Button>
                                    <Button type="submit">
                                      {editingPhoto ? t("admin.photos.update") : t("admin.photos.uploadbutton")}
                                    </Button>
                                  </DialogFooter>
                                </form>
                              </DialogContent>
                            </Dialog>
                            </div>
                          )}
                        </div>

                        {isLoadingPhotos ? (
                          <p className="text-center py-8">{t("admin.photos.loading")}</p>
                        ) : missionPhotos.length === 0 ? (
                          <p className="text-center py-8 text-muted-foreground">No media yet. Upload some photos or videos!</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {missionPhotos.map((photo) => (
                              <Card key={photo.id} className={`overflow-hidden ${selectedPhotos.includes(photo.id) ? 'ring-2 ring-primary' : ''}`}>
                                <div className="relative">
                                  {isSelectMode && (
                                    <div className="absolute top-2 left-2 z-10">
                                      <input
                                        type="checkbox"
                                        checked={selectedPhotos.includes(photo.id)}
                                        onChange={() => togglePhotoSelection(photo.id)}
                                        className="w-5 h-5 cursor-pointer"
                                      />
                                    </div>
                                  )}
                                  {photo.media_type === 'video' && photo.video_url ? (
                                    photo.video_url.includes('youtube.com') || photo.video_url.includes('youtu.be') ? (
                                      <div className="w-full h-48 bg-black flex items-center justify-center">
                                        <span className="text-white">🎥 YouTube Video</span>
                                      </div>
                                    ) : (
                                      <video 
                                        src={photo.video_url}
                                        className="w-full h-48 object-cover"
                                        controls
                                      />
                                    )
                                  ) : (
                                    <img 
                                      src={photo.image_url} 
                                      alt={photo.title}
                                      className="w-full h-48 object-cover"
                                    />
                                  )}
                                </div>
                                <div className="p-4">
                                  <h3 className="font-semibold text-lg mb-2">{photo.title}</h3>
                                  {photo.description && (
                                    <p className="text-sm text-muted-foreground mb-3">{photo.description}</p>
                                  )}
                                  <div className="text-xs text-muted-foreground mb-3">
                                    {t("admin.photos.order")}: {photo.display_order}
                                  </div>
                                  {isAdmin && !isSelectMode && (
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openPhotoDialog(photo)}
                                      >
                                        <Edit className="h-4 w-4 mr-1" />
                                        {t("admin.photos.editbutton")}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => handleDeletePhoto(photo.id, photo.image_url)}
                                      >
                                        <Trash2 className="h-4 w-4 mr-1" />
                                        {t("admin.photos.deletebutton")}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Trip Dialog */}
                <Dialog open={isTripDialogOpen} onOpenChange={setIsTripDialogOpen}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingTrip ? "Edit Trip" : "Create New Trip"}</DialogTitle>
                      <DialogDescription>
                        {editingTrip ? "Update the trip details" : "Add a new trip to organize your mission photos"}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleTripSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="trip-title">Trip Title *</Label>
                        <Input
                          id="trip-title"
                          value={tripForm.title}
                          onChange={(e) => setTripForm({ ...tripForm, title: e.target.value })}
                          required
                          placeholder="e.g., 2024 Summer Medical Mission"
                        />
                      </div>
                      <div>
                        <Label htmlFor="trip-description">Description</Label>
                        <Textarea
                          id="trip-description"
                          value={tripForm.description}
                          onChange={(e) => setTripForm({ ...tripForm, description: e.target.value })}
                          rows={3}
                          placeholder="Brief description of the trip..."
                        />
                      </div>
                      <div>
                        <Label htmlFor="trip-date">Trip Date</Label>
                        <Input
                          id="trip-date"
                          type="date"
                          value={tripForm.trip_date}
                          onChange={(e) => setTripForm({ ...tripForm, trip_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="trip-order">Display Order</Label>
                        <Input
                          id="trip-order"
                          type="number"
                          value={tripForm.display_order}
                          onChange={(e) => setTripForm({ ...tripForm, display_order: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={closeTripDialog}>
                          Cancel
                        </Button>
                        <Button type="submit">
                          {editingTrip ? "Update Trip" : "Create Trip"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </Card>
            </TabsContent>

            <TabsContent value="specialevents">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">{t("admin.specialevents.title")}</h2>
                  {isAdmin && (
                    <Button onClick={() => openEventDialog()}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t("admin.specialevents.createnew")}
                    </Button>
                  )}
                </div>

                {isLoadingEvents ? (
                  <p className="text-center py-8">{t("admin.specialevents.loading")}</p>
                ) : specialEvents.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">{t("admin.specialevents.noevents")}</p>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {specialEvents.map((event) => (
                        <Card 
                          key={event.id} 
                          className={`cursor-pointer hover:shadow-lg transition-shadow ${
                            selectedEvent === event.id ? 'ring-2 ring-primary' : ''
                          }`}
                          onClick={() => {
                            setSelectedEvent(event.id);
                            fetchEventMedia(event.id);
                            setSelectedMedia([]);
                            setIsMediaSelectMode(false);
                          }}
                        >
                          <div className="p-4">
                            <h3 className="font-semibold text-lg mb-2">{event.title}</h3>
                            {event.description && (
                              <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
                            )}
                            {event.event_date && (
                              <p className="text-xs text-muted-foreground mb-3">
                                {t("admin.specialevents.date")}: {formatDateForDisplay(event.event_date)}
                              </p>
                            )}
                            {isAdmin && (
                              <div className="flex gap-2 mt-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEventDialog(event);
                                  }}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  {t("admin.specialevents.edit")}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteEvent(event.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  {t("admin.specialevents.delete")}
                                </Button>
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>

                    {selectedEvent && (
                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold">{t("admin.specialevents.gallery")}</h3>
                          {isAdmin && (
                            <div className="flex gap-2 flex-wrap">
                              <Button 
                                onClick={() => {
                                  setIsMediaSelectMode(!isMediaSelectMode);
                                  setSelectedMedia([]);
                                }} 
                                variant="outline"
                                size="sm"
                              >
                                {isMediaSelectMode ? t("admin.specialevents.cancelselection") : t("admin.specialevents.selectmultiple")}
                              </Button>
                              {isMediaSelectMode && (
                                <>
                                  <Button onClick={selectAllMedia} variant="outline" size="sm">
                                    {t("admin.specialevents.selectall")}
                                  </Button>
                                  <Button onClick={deselectAllMedia} variant="outline" size="sm">
                                    {t("admin.specialevents.deselectall")}
                                  </Button>
                                  <Button 
                                    onClick={handleBulkDeleteMedia} 
                                    variant="destructive"
                                    size="sm"
                                    disabled={selectedMedia.length === 0}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    {t("admin.specialevents.delete")} ({selectedMedia.length})
                                  </Button>
                                </>
                              )}
                              <Button onClick={() => openMediaDialog()} size="sm">
                                <Upload className="w-4 h-4 mr-2" />
                                {t("admin.specialevents.uploadmedia")}
                              </Button>
                            </div>
                          )}
                        </div>

                        {isLoadingMedia ? (
                          <p className="text-center py-8">{t("admin.specialevents.loadingmedia")}</p>
                        ) : eventMedia.length === 0 ? (
                          <p className="text-center py-8 text-muted-foreground">{t("admin.specialevents.nomedia")}</p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {eventMedia.map((media) => (
                              <Card key={media.id} className={`overflow-hidden ${selectedMedia.includes(media.id) ? 'ring-2 ring-primary' : ''}`}>
                                <div className="relative">
                                  {isMediaSelectMode && (
                                    <div className="absolute top-2 left-2 z-10">
                                      <input
                                        type="checkbox"
                                        checked={selectedMedia.includes(media.id)}
                                        onChange={() => toggleMediaSelection(media.id)}
                                        className="w-5 h-5 cursor-pointer"
                                      />
                                    </div>
                                  )}
                                  {media.media_type === 'video' && media.video_url ? (
                                    <video 
                                      src={media.video_url} 
                                      className="w-full h-48 object-cover"
                                      controls
                                    />
                                  ) : (
                                    <img 
                                      src={media.image_url} 
                                      alt={media.title}
                                      className="w-full h-48 object-cover"
                                    />
                                  )}
                                </div>
                                <div className="p-4">
                                  <h4 className="font-semibold mb-2">{media.title}</h4>
                                  {media.description && (
                                    <p className="text-sm text-muted-foreground mb-2">{media.description}</p>
                                  )}
                                  <div className="text-xs text-muted-foreground mb-3">
                                    {t("admin.specialevents.order")}: {media.display_order} | {t("admin.specialevents.type")}: {media.media_type}
                                  </div>
                                  {isAdmin && !isMediaSelectMode && (
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openMediaDialog(media)}
                                      >
                                        <Edit className="h-4 w-4 mr-1" />
                                        {t("admin.specialevents.edit")}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => handleDeleteMedia(media.id, media.image_url)}
                                      >
                                        <Trash2 className="h-4 w-4 mr-1" />
                                        {t("admin.specialevents.delete")}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Event Dialog */}
                <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingEvent ? t("admin.specialevents.editevent") : t("admin.specialevents.createevent")}</DialogTitle>
                      <DialogDescription>
                        {editingEvent ? t("admin.specialevents.updateevent") : t("admin.specialevents.createeventdesc")}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEventSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="event-title">{t("admin.specialevents.eventtitle")} *</Label>
                        <Input
                          id="event-title"
                          value={eventForm.title}
                          onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                          required
                          placeholder="e.g., Christmas 2024, Easter Service"
                        />
                      </div>
                      <div>
                        <Label htmlFor="event-description">{t("admin.specialevents.description")}</Label>
                        <Textarea
                          id="event-description"
                          value={eventForm.description}
                          onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                          rows={3}
                          placeholder={t("admin.specialevents.descriptionplaceholder")}
                        />
                      </div>
                      <div>
                        <Label htmlFor="event-date">{t("admin.specialevents.eventdate")}</Label>
                        <Input
                          id="event-date"
                          type="date"
                          value={eventForm.event_date}
                          onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="event-order">{t("admin.specialevents.displayorder")}</Label>
                        <Input
                          id="event-order"
                          type="number"
                          value={eventForm.display_order}
                          onChange={(e) => setEventForm({ ...eventForm, display_order: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={closeEventDialog}>
                          {t("admin.specialevents.cancel")}
                        </Button>
                        <Button type="submit">
                          {editingEvent ? t("admin.specialevents.update") : t("admin.specialevents.create")}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Media Upload Dialog */}
                <Dialog open={isMediaDialogOpen} onOpenChange={setIsMediaDialogOpen}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingMedia ? t("admin.specialevents.editmedia") : t("admin.specialevents.uploadmediatitle")}</DialogTitle>
                      <DialogDescription>
                        {editingMedia 
                          ? t("admin.specialevents.updatemedia")
                          : t("admin.specialevents.uploadmediadesc")}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={editingMedia ? handleEditMedia : handleMediaUpload} className="space-y-4">
                      {!editingMedia && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="youtube-url" className="text-base font-semibold">
                              {t("admin.specialevents.youtubeurl")}
                            </Label>
                            <Input
                              id="youtube-url"
                              type="url"
                              placeholder={t("admin.specialevents.youtubeurlplaceholder")}
                              value={mediaForm.video_url}
                              onChange={(e) => setMediaForm({ ...mediaForm, video_url: e.target.value })}
                              className="font-mono text-sm"
                            />
                            <p className="text-sm text-muted-foreground">
                              {t("admin.specialevents.youtubehelp")}
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="media-file" className="text-base font-semibold">
                              {mediaForm.video_url 
                                ? t("admin.specialevents.thumbnailimage")
                                : t("admin.specialevents.selectfiles") + " *"}
                            </Label>
                            <Input
                              id="media-file"
                              type="file"
                              accept={mediaForm.video_url ? "image/*" : "image/*,video/*"}
                              multiple={!mediaForm.video_url}
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                setUploadingFiles(files);
                              }}
                              required={!mediaForm.video_url}
                              className="cursor-pointer"
                            />
                            {mediaForm.video_url && (
                              <p className="text-sm text-muted-foreground">
                                {t("admin.specialevents.customthumbnail")}
                              </p>
                            )}
                            {uploadingFiles.length > 0 && (
                              <div className="mt-3 p-4 bg-accent/50 border-2 border-primary/20 rounded-lg">
                                <p className="text-sm font-semibold mb-2 text-foreground">
                                  {uploadingFiles.length} {t("admin.specialevents.filesselected")}
                                </p>
                                <ul className="text-sm space-y-2 max-h-32 overflow-y-auto">
                                  {uploadingFiles.map((file, idx) => (
                                    <li key={idx} className="flex items-center gap-2 text-foreground">
                                      <span>{file.type.startsWith('video/') ? '🎥' : '🖼️'}</span>
                                      <span className="font-medium">{file.name}</span>
                                    </li>
                                  ))}
                                </ul>
                                {uploadProgress > 0 && (
                                  <div className="mt-4 space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-foreground font-medium">
                                        {t("admin.specialevents.uploading")} {currentUploadingFile}
                                      </span>
                                      <span className="text-muted-foreground">
                                        {Math.round(uploadProgress)}%
                                      </span>
                                    </div>
                                    <Progress value={uploadProgress} className="h-2" />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      <div>
                        <Label htmlFor="media-title">{t("admin.specialevents.mediatitle")} {!editingMedia && uploadingFiles.length <= 1 && '*'}</Label>
                        <Input
                          id="media-title"
                          value={mediaForm.title}
                          onChange={(e) => setMediaForm({ ...mediaForm, title: e.target.value })}
                          required={!!editingMedia || uploadingFiles.length <= 1}
                          placeholder={uploadingFiles.length > 1 ? t("admin.specialevents.mediatitlerequired") : ""}
                        />
                      </div>
                      <div>
                        <Label htmlFor="media-description">{t("admin.specialevents.mediadescription")}</Label>
                        <Textarea
                          id="media-description"
                          value={mediaForm.description}
                          onChange={(e) => setMediaForm({ ...mediaForm, description: e.target.value })}
                          rows={3}
                          placeholder={t("admin.specialevents.mediadescriptionplaceholder")}
                        />
                      </div>
                      <div>
                        <Label htmlFor="media-order">{t("admin.specialevents.mediaorder")}</Label>
                        <Input
                          id="media-order"
                          type="number"
                          value={mediaForm.display_order}
                          onChange={(e) => setMediaForm({ ...mediaForm, display_order: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={closeMediaDialog}>
                          {t("admin.specialevents.cancel")}
                        </Button>
                        <Button type="submit">
                          {editingMedia ? t("admin.specialevents.update") : t("admin.specialevents.savemedia")}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Blog Translations Dialog */}
          <Dialog open={isBlogTranslationsDialogOpen} onOpenChange={setIsBlogTranslationsDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("admin.blog.translations.title")}</DialogTitle>
                <DialogDescription>
                  {t("admin.blog.translations.desc")}
                </DialogDescription>
              </DialogHeader>
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="en">{t("admin.sermons.translations.english")}</TabsTrigger>
                  <TabsTrigger value="ru">{t("admin.sermons.translations.russian")}</TabsTrigger>
                  <TabsTrigger value="es">{t("admin.sermons.translations.spanish")}</TabsTrigger>
                </TabsList>
                
                {['en', 'ru', 'es'].map((lang) => (
                  <TabsContent key={lang} value={lang} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor={`blog-title-${lang}`}>{t("admin.blog.field.title")} *</Label>
                      <Input
                        id={`blog-title-${lang}`}
                        value={blogTranslations[lang]?.title || ""}
                        onChange={(e) => updateBlogTranslation(lang, 'title', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`blog-author-${lang}`}>{t("admin.blog.field.author")} *</Label>
                      <Input
                        id={`blog-author-${lang}`}
                        value={blogTranslations[lang]?.author || ""}
                        onChange={(e) => updateBlogTranslation(lang, 'author', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`blog-excerpt-${lang}`}>{t("admin.blog.field.excerpt")}</Label>
                      <Textarea
                        id={`blog-excerpt-${lang}`}
                        value={blogTranslations[lang]?.excerpt || ""}
                        onChange={(e) => updateBlogTranslation(lang, 'excerpt', e.target.value)}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`blog-content-${lang}`}>{t("admin.blog.field.content")} *</Label>
                      <Textarea
                        id={`blog-content-${lang}`}
                        value={blogTranslations[lang]?.content || ""}
                        onChange={(e) => updateBlogTranslation(lang, 'content', e.target.value)}
                        rows={8}
                      />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsBlogTranslationsDialogOpen(false)}>
                  {t("admin.blog.cancel")}
                </Button>
                <Button type="button" onClick={handleSaveBlogTranslations}>
                  {t("admin.blog.translations.save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Course Translations Dialog */}
          <Dialog open={isCourseTranslationsDialogOpen} onOpenChange={setIsCourseTranslationsDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("admin.bibleschool.translations.title")}</DialogTitle>
                <DialogDescription>
                  {t("admin.bibleschool.translations.desc")}
                </DialogDescription>
              </DialogHeader>
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="en">{t("admin.sermons.translations.english")}</TabsTrigger>
                  <TabsTrigger value="ru">{t("admin.sermons.translations.russian")}</TabsTrigger>
                  <TabsTrigger value="es">{t("admin.sermons.translations.spanish")}</TabsTrigger>
                </TabsList>
                
                {['en', 'ru', 'es'].map((lang) => (
                  <TabsContent key={lang} value={lang} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor={`course-title-${lang}`}>{t("admin.bibleschool.field.title")} *</Label>
                      <Input
                        id={`course-title-${lang}`}
                        value={courseTranslations[lang]?.title || ""}
                        onChange={(e) => updateCourseTranslation(lang, 'title', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`course-instructor-${lang}`}>{t("admin.bibleschool.field.instructor")} *</Label>
                      <Input
                        id={`course-instructor-${lang}`}
                        value={courseTranslations[lang]?.instructor || ""}
                        onChange={(e) => updateCourseTranslation(lang, 'instructor', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`course-description-${lang}`}>{t("admin.bibleschool.field.description")} *</Label>
                      <Textarea
                        id={`course-description-${lang}`}
                        value={courseTranslations[lang]?.description || ""}
                        onChange={(e) => updateCourseTranslation(lang, 'description', e.target.value)}
                        rows={4}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`course-duration-${lang}`}>{t("admin.bibleschool.field.duration")} *</Label>
                      <Input
                        id={`course-duration-${lang}`}
                        value={courseTranslations[lang]?.duration || ""}
                        onChange={(e) => updateCourseTranslation(lang, 'duration', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`course-level-${lang}`}>{t("admin.bibleschool.field.level")} *</Label>
                      <Input
                        id={`course-level-${lang}`}
                        value={courseTranslations[lang]?.level || ""}
                        onChange={(e) => updateCourseTranslation(lang, 'level', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`course-schedule-${lang}`}>{t("admin.bibleschool.field.schedule")}</Label>
                      <Input
                        id={`course-schedule-${lang}`}
                        value={courseTranslations[lang]?.schedule || ""}
                        onChange={(e) => updateCourseTranslation(lang, 'schedule', e.target.value)}
                      />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCourseTranslationsDialogOpen(false)}>
                  {t("admin.bibleschool.cancel")}
                </Button>
                <Button type="button" onClick={handleSaveCourseTranslations}>
                  {t("admin.bibleschool.translations.save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>


          {/* Sermon Translations Dialog */}
          <Dialog open={isTranslationsDialogOpen} onOpenChange={setIsTranslationsDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("admin.sermons.translations.title")}</DialogTitle>
                <DialogDescription>
                  {t("admin.sermons.translations.description")}
                </DialogDescription>
              </DialogHeader>
              <Tabs defaultValue="en" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="en">{t("admin.sermons.translations.english")}</TabsTrigger>
                  <TabsTrigger value="ru">{t("admin.sermons.translations.russian")}</TabsTrigger>
                  <TabsTrigger value="es">{t("admin.sermons.translations.spanish")}</TabsTrigger>
                </TabsList>
                
                {['en', 'ru', 'es'].map((lang) => (
                  <TabsContent key={lang} value={lang} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor={`title-${lang}`}>{t("admin.sermons.title.label")} *</Label>
                      <Input
                        id={`title-${lang}`}
                        value={translations[lang]?.title || ""}
                        onChange={(e) => updateTranslation(lang, 'title', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`speaker-${lang}`}>{t("admin.sermons.speaker.label")} *</Label>
                      <Input
                        id={`speaker-${lang}`}
                        value={translations[lang]?.speaker || ""}
                        onChange={(e) => updateTranslation(lang, 'speaker', e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor={`scripture-${lang}`}>{t("admin.sermons.scripture.label")}</Label>
                      <Textarea
                        id={`scripture-${lang}`}
                        value={translations[lang]?.scripture_reference || ""}
                        onChange={(e) => updateTranslation(lang, 'scripture_reference', e.target.value)}
                        placeholder={t("admin.sermons.scripture.placeholder")}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`description-${lang}`}>{t("admin.sermons.description.label")}</Label>
                      <Textarea
                        id={`description-${lang}`}
                        value={translations[lang]?.description || ""}
                        onChange={(e) => updateTranslation(lang, 'description', e.target.value)}
                        rows={4}
                      />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsTranslationsDialogOpen(false)}>
                  {t("admin.sermons.cancel")}
                </Button>
                <Button type="button" onClick={handleSaveTranslations}>
                  {t("admin.sermons.translations.save")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
